import Decimal from 'decimal.js';
export { default as Decimal } from 'decimal.js';
import { z } from 'zod';

// src/types/enums.ts
var LOCALES = ["fr", "en"];
var DEFAULT_LOCALE = "fr";
var ROLES = ["caregiver", "clinician", "owner_admin"];
var MEDICATION_CONCEPTS = [
  "pyrimethamine",
  // Fansidar
  "sulfadiazine",
  // Adiazine
  "folinic_acid",
  // Leucovorin / Lederfoline — NOT folic acid (Safety §6)
  "folic_acid",
  // AcFol / acide folique — NOT folinic acid (Safety §6)
  "vitamin_d",
  // ADEC
  "supplemental_hydration",
  "antibiotic",
  // e.g. Oroken — adjunct/temporary
  "custom"
];
var FOLATE_CONCEPTS = ["folinic_acid", "folic_acid"];
var MEDICATION_FORMS = [
  "tablet",
  "suspension",
  "compounded_liquid",
  "capsule",
  "drops",
  "powder",
  "other"
];
var ROUTES = ["oral", "topical", "other"];
var DOSE_UNITS = ["mg", "mL", "tablet", "tablet_fraction", "drops", "IU", "other"];
var FREQUENCY_TYPES = [
  "once_daily",
  "twice_daily",
  // BID — distinct times (e.g. morning + 20h)
  "three_times_daily",
  // TID
  "weekly",
  "three_times_weekly",
  "every_other_day",
  // "1 soir sur 2"
  "every_x_hours",
  "specific_weekdays",
  "custom"
];
var WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
var ORDER_APPROVAL_STATUSES = [
  "draft",
  // proposed / imported, not yet clinician-signed (Appendix A seeds)
  "pending_review",
  "approved",
  // active order — only a clinician can reach this
  "rejected",
  "suspended",
  // held by doctor
  "discontinued"
];
var ADMINISTRATION_STATUSES = [
  "taken",
  "late",
  "missed",
  "vomited_after_dose",
  "partial",
  "refused",
  "held_by_doctor",
  "not_applicable",
  // slot not scheduled this day
  "unknown"
];
var ADHERENT_STATUSES = ["taken", "late", "partial"];
var NONADHERENT_STATUSES = [
  "missed",
  "refused",
  "vomited_after_dose"
];
var LAB_STATUSES = [
  "ordered",
  "drawn",
  "pending",
  // sample sent, result not back (sometimes sent to France)
  "received",
  "redraw_needed",
  // e.g. holiday forced a redraw
  "rejected",
  "reviewed"
  // doctor reviewed + signed
];
var LAB_INTERPRETATIONS = [
  "no_change",
  "repeat",
  "dose_change",
  "temporary_hold",
  "stop",
  "consult_hematology",
  "other"
];
var ANALYTE_KEYS = [
  "hemoglobin",
  // Hb
  "wbc",
  // GB / WBC
  "neutrophils_abs",
  // PNN absolute — the clinical driver of this case
  "neutrophils_pct",
  // PNN %
  "platelets",
  // Plaquettes
  "lymphocytes_pct",
  "lymphocytes_abs",
  "reticulocytes",
  "bilirubin",
  "alt",
  "ast",
  "creatinine",
  "urea",
  "crp",
  "custom"
];
var ANALYTE_UNITS = [
  "g/dL",
  "g/L",
  "per_mm3",
  // /mm³
  "10e3/uL",
  // 10³/µL
  "10e9/L",
  // 10⁹/L
  "percent",
  "mg/dL",
  "umol/L",
  "mg/L",
  "IU/L",
  "other"
];
var TREND_DIRECTIONS = ["up", "down", "flat", "unknown"];
var SEROLOGY_MARKERS = ["igg", "igm", "iga", "pcr"];
var SEROLOGY_QUALITATIVE = ["positive", "negative", "equivocal", "not_done"];
var WEIGHT_SOURCES = ["parent", "clinic", "hospital", "pharmacy", "imported"];
var EXAM_TYPES = [
  "nfs",
  // blood safety
  "weight_dose_review",
  "dilated_fundus",
  // eyes — chorioretinitis
  "etf",
  // transfontanelle ultrasound (brain)
  "mri",
  "baer_pea",
  // hearing
  "pediatric_consult",
  "serology",
  "other"
];
var APPOINTMENT_STATUSES = [
  "scheduled",
  "completed",
  "missed",
  "cancelled",
  "rescheduled"
];
var APPOINTMENT_PARENT_CONFIRM = ["booked", "attended", "missed"];
var SURVEILLANCE_STATUSES = ["upcoming", "due", "overdue", "done"];
var RULE_TYPES = [
  "threshold",
  "due_date",
  "overdue",
  "trend",
  "missing_data",
  "symptom_triggered",
  "doctor_review_needed",
  "custom"
];
var SEVERITIES = ["info", "warning", "urgent", "critical"];
var RECIPIENTS = ["parent", "doctor", "both"];
var COMPARATORS = ["lt", "lte", "gt", "gte", "eq", "neq", "out_of_range"];
var ALERT_STATUSES = ["open", "acknowledged", "resolved", "dismissed"];
var REMINDER_STATES = [
  "scheduled",
  "sent",
  "delivered",
  "read",
  "acknowledged",
  "completed",
  "snoozed",
  "escalated",
  "expired",
  "canceled"
];
var REMINDER_TYPES = [
  "medication_due_soon",
  "medication_now",
  "medication_overdue",
  "medication_missed",
  "refill",
  "weight_check",
  "dose_review",
  "nfs_due",
  "nfs_overdue",
  "nfs_pending",
  "redraw_needed",
  "threshold_breach",
  "symptom_triggered",
  "fundus_due",
  "etf_due",
  "mri_due",
  "pea_due",
  "pediatric_due",
  "doctor_review_needed",
  "pending_question",
  "attachment_for_review",
  "treatment_milestone",
  "daily_log_nudge"
];
var NOTIFICATION_CHANNELS = [
  "local",
  // on-device (backbone)
  "whatsapp",
  "in_app",
  "email",
  "web_push",
  "sms"
  // deferred / stub
];
var G6PD_STATUSES = ["unknown", "pending", "normal", "abnormal", "not_applicable"];
var DIAGNOSIS_STATUSES = [
  "suspected",
  "confirmed",
  "treating",
  "resolved",
  "ruled_out"
];
var SEX_VALUES = ["male", "female", "unknown"];
var SYMPTOM_KEYS = [
  "fever",
  "pallor",
  "bruising",
  "rash",
  "vomiting",
  "vomiting_after_dose",
  "poor_feeding",
  "low_wet_diapers",
  "dehydration_concern",
  "lethargy",
  "irritability",
  "other"
];
var FEEDING_TYPES = ["breast", "formula", "mixed", "solid", "other"];
var ATTACHMENT_KINDS = [
  "lab_pdf",
  "lab_photo",
  "appointment_report",
  "imaging_report",
  "eye_report",
  "hearing_report",
  "prescription_photo",
  "pharmacy_label",
  "dose_photo",
  "follow_sheet",
  // photo/PDF of a filled paper follow sheet (fiche de suivi)
  "other"
];
var CLINICAL_NOTE_CATEGORIES = ["general", "g6pd", "imaging", "exam", "lab", "other"];
var REPORT_TYPES = [
  "consultation_pdf",
  "weekly_sheet",
  "adherence",
  "dose_history",
  "twelve_month_followup",
  "followup_checklist"
];
var EXPORT_FORMATS = ["csv", "xlsx", "json", "encrypted_zip"];
var AUDIT_ACTIONS = [
  "create",
  "update",
  "soft_delete",
  "view",
  // record views are audited
  "download",
  // attachment downloads are audited
  "sign",
  "approve",
  "reject",
  "suspend",
  "discontinue",
  "acknowledge",
  "login",
  "logout",
  "invite",
  "config_change",
  "backup",
  "restore",
  "export"
];
var INVITATION_STATUSES = ["pending", "accepted", "revoked", "expired"];

// src/constants.ts
var DEFAULT_BRAND_NAMES = {
  pyrimethamine: { fr: "Pyrim\xE9thamine (Fansidar)", en: "Pyrimethamine (Fansidar)" },
  sulfadiazine: { fr: "Sulfadiazine (Adiazine)", en: "Sulfadiazine (Adiazine)" },
  folinic_acid: { fr: "Acide folinique (Lederfoline)", en: "Folinic acid (Leucovorin/Lederfoline)" },
  folic_acid: { fr: "Acide folique (AcFol)", en: "Folic acid (AcFol)" },
  vitamin_d: { fr: "Vitamine D (ADEC)", en: "Vitamin D (ADEC)" },
  supplemental_hydration: { fr: "Hydratation suppl\xE9mentaire", en: "Supplemental hydration" },
  antibiotic: { fr: "Antibiotique", en: "Antibiotic" },
  custom: { fr: "M\xE9dicament personnalis\xE9", en: "Custom medication" }
};
var DEFAULT_MEASURABLE_INCREMENT = {
  tablet: "0.25",
  // quarter tablet
  capsule: "1",
  suspension: "0.1",
  // 0.1 mL
  compounded_liquid: "0.1",
  // 0.1 mL
  drops: "1",
  powder: "1",
  other: "0.01"
};
var MS_PER_MINUTE = 6e4;
var MS_PER_HOUR = 36e5;
var MS_PER_DAY = 864e5;
var DEFAULT_LATE_WINDOW_MINUTES = 60;
var DEFAULT_PENDING_RESULT_DAYS = 7;
var DEFAULT_STALE_WEIGHT_DAYS = 14;
var APP_NAME = "ToxoFollow";
Decimal.set({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -9e15,
  toExpPos: 9e15
});
function dec(value) {
  if (value instanceof Decimal) return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new RangeError(`dec(): non-finite number ${value}`);
    }
    return new Decimal(value.toString());
  }
  const trimmed = value.trim();
  if (trimmed === "") throw new RangeError("dec(): empty string");
  return new Decimal(trimmed);
}
function isDecimalLike(value) {
  if (value instanceof Decimal) return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") {
    try {
      new Decimal(value.trim());
      return value.trim() !== "";
    } catch {
      return false;
    }
  }
  return false;
}
var ROUNDING_MAP = {
  half_up: Decimal.ROUND_HALF_UP,
  half_even: Decimal.ROUND_HALF_EVEN,
  up: Decimal.ROUND_UP,
  down: Decimal.ROUND_DOWN,
  ceil: Decimal.ROUND_CEIL,
  floor: Decimal.ROUND_FLOOR
};
function roundTo(value, places, mode = "half_up") {
  return dec(value).toDecimalPlaces(places, ROUNDING_MAP[mode]);
}
function roundToIncrement(value, increment, direction = "nearest") {
  const v = dec(value);
  const inc = dec(increment);
  if (inc.lte(0)) throw new RangeError("roundToIncrement(): increment must be > 0");
  const quotient = v.div(inc);
  let rounded;
  switch (direction) {
    case "down":
      rounded = quotient.toDecimalPlaces(0, Decimal.ROUND_DOWN);
      break;
    case "up":
      rounded = quotient.toDecimalPlaces(0, Decimal.ROUND_UP);
      break;
    default:
      rounded = quotient.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  }
  return rounded.times(inc);
}
function add(a, b) {
  return dec(a).plus(dec(b));
}
function sub(a, b) {
  return dec(a).minus(dec(b));
}
function mul(a, b) {
  return dec(a).times(dec(b));
}
function div(a, b) {
  const d = dec(b);
  if (d.isZero()) throw new RangeError("div(): division by zero");
  return dec(a).div(d);
}
function toStorageString(value) {
  return dec(value).toFixed();
}

// src/decimal/units.ts
var COUNT_FACTOR_TO_PER_MM3 = {
  per_mm3: "1",
  // base
  "10e3/uL": "1000",
  // 10³/µL = 1000 /µL = 1000 /mm³
  "10e9/L": "1000"
  // 10⁹/L = 1000 /µL = 1000 /mm³
};
var HB_FACTOR_TO_G_PER_L = {
  "g/L": "1",
  "g/dL": "10"
  // 1 g/dL = 10 g/L
};
function unitFamily(unit) {
  if (unit in COUNT_FACTOR_TO_PER_MM3) return "count";
  if (unit in HB_FACTOR_TO_G_PER_L) return "hb";
  if (unit === "percent") return "percent";
  return "incompatible";
}
function areUnitsCompatible(a, b) {
  const fa = unitFamily(a);
  const fb = unitFamily(b);
  return fa === fb && fa !== "incompatible";
}
function convertAnalyte(value, from, to) {
  if (from === to) return dec(value);
  const family = unitFamily(from);
  if (!areUnitsCompatible(from, to)) {
    throw new RangeError(`convertAnalyte(): cannot convert ${from} -> ${to}`);
  }
  if (family === "count") {
    const fFrom = dec(COUNT_FACTOR_TO_PER_MM3[from]);
    const fTo = dec(COUNT_FACTOR_TO_PER_MM3[to]);
    return dec(value).times(fFrom).div(fTo);
  }
  if (family === "hb") {
    const fFrom = dec(HB_FACTOR_TO_G_PER_L[from]);
    const fTo = dec(HB_FACTOR_TO_G_PER_L[to]);
    return dec(value).times(fFrom).div(fTo);
  }
  return dec(value);
}
function gramsToKg(grams) {
  return dec(grams).div(1e3);
}
function kgToGrams(kg) {
  return dec(kg).times(1e3);
}
function absoluteFromPercent(wbcPerMm3, pct2) {
  return dec(wbcPerMm3).times(dec(pct2)).div(100);
}

// src/datetime/datetime.ts
var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
var TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
function isCalendarDate(s) {
  if (!DATE_RE.test(s)) return false;
  const d = /* @__PURE__ */ new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && toCalendarDate(d) === s;
}
function isClockTime(s) {
  return TIME_RE.test(s);
}
function parseCalendarDate(s) {
  if (!DATE_RE.test(s)) throw new RangeError(`Invalid calendar date: ${s}`);
  const d = /* @__PURE__ */ new Date(`${s}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) throw new RangeError(`Invalid calendar date: ${s}`);
  return d;
}
function toCalendarDate(d) {
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(date, days) {
  const d = parseCalendarDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return toCalendarDate(d);
}
function addMonths(date, months) {
  const d = parseCalendarDate(date);
  const targetMonth = d.getUTCMonth() + months;
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(targetMonth);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return toCalendarDate(d);
}
function addWeeks(date, weeks) {
  return addDays(date, weeks * 7);
}
function diffDays(a, b) {
  const ms = parseCalendarDate(b).getTime() - parseCalendarDate(a).getTime();
  return Math.round(ms / 864e5);
}
function diffWeeks(a, b) {
  return Math.floor(diffDays(a, b) / 7);
}
function ageInMonths(dob, on) {
  const d0 = parseCalendarDate(dob);
  const d1 = parseCalendarDate(on);
  let months = (d1.getUTCFullYear() - d0.getUTCFullYear()) * 12 + (d1.getUTCMonth() - d0.getUTCMonth());
  if (d1.getUTCDate() < d0.getUTCDate()) months -= 1;
  return Math.max(0, months);
}
function compareDates(a, b) {
  const ta = parseCalendarDate(a).getTime();
  const tb = parseCalendarDate(b).getTime();
  return ta < tb ? -1 : ta > tb ? 1 : 0;
}
function minDate(a, b) {
  return compareDates(a, b) <= 0 ? a : b;
}
function maxDate(a, b) {
  return compareDates(a, b) >= 0 ? a : b;
}
function weekdayOf(date) {
  const idx = parseCalendarDate(date).getUTCDay();
  const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return map[idx];
}
function clockToMinutes(t2) {
  const m = TIME_RE.exec(t2);
  if (!m) throw new RangeError(`Invalid time: ${t2}`);
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}
function combineToInstant(date, time, tzOffsetMinutes = 0) {
  const base = parseCalendarDate(date).getTime();
  const minutes = clockToMinutes(time);
  return new Date(base + (minutes - tzOffsetMinutes) * 6e4);
}
function diffMinutes(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 6e4);
}

// src/datetime/day-cursor.ts
var DAY_CURSOR_FALLBACK_DAYS = 90;
function dayBounds(treatmentStart, today) {
  const start = treatmentStart ?? addDays(today, -DAY_CURSOR_FALLBACK_DAYS);
  return { min: minDate(start, today), max: today };
}
function clampDay(day, b) {
  if (compareDates(day, b.min) < 0) return b.min;
  if (compareDates(day, b.max) > 0) return b.max;
  return day;
}
function stepDay(day, delta, b) {
  return clampDay(addDays(clampDay(day, b), delta), b);
}
function canStepDay(day, delta, b) {
  const from = clampDay(day, b);
  return stepDay(from, delta, b) !== from;
}

// src/datetime/parse-clock.ts
function pad(n) {
  return String(n).padStart(2, "0");
}
function parseClock(raw) {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const s = String(raw).trim().toLowerCase().replace(/\s/g, "");
  if (!s) return null;
  let h;
  let m;
  const sep = s.match(/^(\d{1,2})[h:.](\d{0,2})$/);
  if (sep) {
    h = Number(sep[1]);
    m = sep[2] ? Number(sep[2]) : 0;
  } else if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      h = Number(s);
      m = 0;
    } else if (s.length === 3) {
      h = Number(s.slice(0, 1));
      m = Number(s.slice(1));
    } else {
      h = Number(s.slice(0, 2));
      m = Number(s.slice(2));
    }
  } else {
    return null;
  }
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}
function shiftClock(time, deltaMinutes) {
  const [h, m] = time.split(":").map((x) => parseInt(x, 10));
  const total = (h ?? 0) * 60 + (m ?? 0) + deltaMinutes;
  const wrapped = (total % 1440 + 1440) % 1440;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
}

// src/dose-entry/slots.ts
function dayPartOf(time) {
  const h = Number(time.slice(0, 2));
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}
function groupDosesBySlot(doses, timeOf) {
  const byTime = /* @__PURE__ */ new Map();
  for (const d of doses) {
    const time = timeOf(d);
    const bucket = byTime.get(time);
    if (bucket) bucket.push(d);
    else byTime.set(time, [d]);
  }
  const times = [...byTime.keys()].sort((a, b) => clockToMinutes(a) - clockToMinutes(b));
  return times.map((time, i) => {
    const part = dayPartOf(time);
    return {
      time,
      part,
      showDivider: i > 0 && dayPartOf(times[i - 1]) !== part,
      items: byTime.get(time)
    };
  });
}
var DOSE_GRACE_MINUTES = 60;
function deriveGivenStatus(scheduled, actual, graceMinutes = DOSE_GRACE_MINUTES) {
  if (!scheduled || !actual) return "taken";
  const raw = clockToMinutes(actual) - clockToMinutes(scheduled);
  let delta = (raw % 1440 + 1440) % 1440;
  if (delta > 720) delta -= 1440;
  return delta > graceMinutes ? "late" : "taken";
}
var REASON_STATUS = {
  refused: "refused",
  forgotten: "missed",
  vomited: "vomited_after_dose",
  partial: "partial",
  other: "unknown"
};

// src/sync/merge.ts
function mergeDecision(local) {
  if (local == null) return "insert";
  return local.syncState === "synced" ? "overwrite" : "skip";
}
function nonBlank(v) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s.length > 0 ? s : null;
}
function mergeKey(server) {
  const client = nonBlank(server.clientId);
  if (client) return client;
  const id = nonBlank(server.id);
  return id ? `srv:${id}` : null;
}

// src/recurrence/recurrence.ts
function withinBounds(spec, date) {
  if (compareDates(date, spec.startDate) < 0) return false;
  if (spec.endDate && compareDates(date, spec.endDate) > 0) return false;
  return true;
}
function anchor(spec) {
  return spec.anchorDate ?? spec.startDate;
}
function sortTimes(times) {
  return [...new Set(times)].sort((a, b) => clockToMinutes(a) - clockToMinutes(b));
}
function deriveEveryXHours(spec) {
  const hours = spec.everyXHours ?? 24;
  if (hours <= 0 || hours > 24) return spec.times.length ? sortTimes(spec.times) : ["08:00"];
  const startMin = spec.times.length ? clockToMinutes(sortTimes(spec.times)[0]) : 8 * 60;
  const out = [];
  for (let m = startMin; m < 24 * 60; m += hours * 60) {
    const h = Math.floor(m / 60).toString().padStart(2, "0");
    const mm = (m % 60).toString().padStart(2, "0");
    out.push(`${h}:${mm}`);
  }
  return out.length ? out : ["08:00"];
}
function occursOn(spec, date) {
  if (!withinBounds(spec, date)) return false;
  switch (spec.frequencyType) {
    case "once_daily":
    case "twice_daily":
    case "three_times_daily":
    case "every_x_hours":
      return true;
    case "weekly": {
      const d = diffDays(anchor(spec), date);
      return d >= 0 && d % 7 === 0;
    }
    case "every_other_day": {
      const interval = spec.intervalDays && spec.intervalDays > 0 ? spec.intervalDays : 2;
      const d = diffDays(anchor(spec), date);
      return d >= 0 && d % interval === 0;
    }
    case "three_times_weekly":
    case "specific_weekdays": {
      const wd = weekdayOf(date);
      return (spec.weekdays ?? []).includes(wd);
    }
    case "custom": {
      if (spec.intervalDays && spec.intervalDays > 0) {
        const d = diffDays(anchor(spec), date);
        return d >= 0 && d % spec.intervalDays === 0;
      }
      if (spec.weekdays && spec.weekdays.length) {
        return spec.weekdays.includes(weekdayOf(date));
      }
      return true;
    }
    default:
      return false;
  }
}
function timesOn(spec, date) {
  if (!occursOn(spec, date)) return [];
  if (spec.frequencyType === "every_x_hours") {
    return spec.times.length ? sortTimes(spec.times) : deriveEveryXHours(spec);
  }
  if (spec.times.length === 0) {
    return ["08:00"];
  }
  return sortTimes(spec.times);
}
function occurrencesBetween(spec, from, to) {
  if (compareDates(from, to) > 0) return [];
  const out = [];
  let cursor = from;
  for (let i = 0; i < 2e3 && compareDates(cursor, to) <= 0; i++) {
    if (occursOn(spec, cursor)) out.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return out;
}
function nextOccurrence(spec, from) {
  let cursor = from;
  for (let i = 0; i < 730; i++) {
    if (occursOn(spec, cursor)) return cursor;
    if (spec.endDate && compareDates(cursor, spec.endDate) > 0) return null;
    cursor = addDays(cursor, 1);
  }
  return null;
}

// src/schedule/schedule.ts
function scheduledDosesForDate(orders, date) {
  const slots = [];
  for (const order of orders) {
    const times = timesOn(order.schedule, date);
    for (const time of times) {
      slots.push({
        orderId: order.orderId,
        concept: order.concept,
        date,
        time,
        doseAmount: order.doseAmount ?? null,
        doseUnit: order.doseUnit ?? null,
        doseWindowMinutes: order.schedule.doseWindowMinutes ?? null
      });
    }
  }
  slots.sort(
    (a, b) => clockToMinutes(a.time) - clockToMinutes(b.time) || a.concept.localeCompare(b.concept) || a.orderId.localeCompare(b.orderId)
  );
  return slots;
}
function scheduledDosesForRange(orders, dates) {
  const out = {};
  for (const date of dates) {
    out[date] = scheduledDosesForDate(orders, date);
  }
  return out;
}
function conceptsInSchedule(orders, dates) {
  const set = /* @__PURE__ */ new Set();
  for (const date of dates) {
    for (const slot of scheduledDosesForDate(orders, date)) set.add(slot.concept);
  }
  return [...set];
}

// src/schedule/surveillance.ts
function phaseOccurrenceDates(dob, phase, horizonDate) {
  const out = [];
  const phaseStart = addMonths(dob, phase.fromMonth);
  const phaseEnd = phase.toMonth !== void 0 ? addMonths(dob, phase.toMonth) : horizonDate;
  const effectiveEnd = compareDates(phaseEnd, horizonDate) < 0 ? phaseEnd : horizonDate;
  if (phase.atMonths && phase.atMonths.length) {
    for (const m of phase.atMonths) {
      const d = addMonths(dob, m);
      if (compareDates(d, horizonDate) <= 0) out.push({ date: d, label: phase.label });
    }
    return out;
  }
  const stepDays = phase.everyDays;
  const stepWeeks = phase.everyWeeks;
  const stepMonths = phase.everyMonths;
  let cursor = phaseStart;
  for (let i = 0; i < 400 && compareDates(cursor, effectiveEnd) <= 0; i++) {
    out.push({ date: cursor, label: phase.label });
    if (stepMonths && stepMonths > 0) cursor = addMonths(cursor, stepMonths);
    else if (stepWeeks && stepWeeks > 0) cursor = addDays(cursor, stepWeeks * 7);
    else if (stepDays && stepDays > 0) cursor = addDays(cursor, stepDays);
    else break;
  }
  return out;
}
function classify(dueDate, today, completed, examType, opts) {
  let best;
  for (const c of completed) {
    if (c.examType !== examType) continue;
    const delta = Math.abs(diffDays(dueDate, c.date));
    if (delta <= opts.matchToleranceDays && (!best || delta < best.absDelta)) {
      best = { id: c.id, absDelta: delta };
    }
  }
  if (best) return { status: "done", matchedCompletedId: best.id };
  const daysUntil = diffDays(today, dueDate);
  if (daysUntil < -opts.defaultGraceDays) return { status: "overdue" };
  if (daysUntil <= opts.dueWindowDays) return { status: "due" };
  return { status: "upcoming" };
}
function buildSurveillanceCalendar(rules, completed, options) {
  const horizonMonths = options.horizonMonths ?? 12;
  const dueWindowDays = options.dueWindowDays ?? 3;
  const defaultGraceDays = options.defaultGraceDays ?? 0;
  const matchToleranceDays = options.matchToleranceDays ?? 10;
  const horizonDate = addMonths(options.dob, horizonMonths);
  const occurrences = [];
  const usedCompleted = /* @__PURE__ */ new Set();
  for (const rule of rules) {
    if (!rule.enabled) continue;
    const dates = [];
    for (const phase of rule.phases) {
      dates.push(...phaseOccurrenceDates(options.dob, phase, horizonDate));
    }
    const seen = /* @__PURE__ */ new Set();
    const uniqueDates = dates.filter((d) => {
      if (seen.has(d.date)) return false;
      seen.add(d.date);
      return true;
    });
    uniqueDates.sort((a, b) => compareDates(a.date, b.date));
    for (const { date, label } of uniqueDates) {
      const cls = classify(date, options.today, completed, rule.examType, {
        dueWindowDays,
        defaultGraceDays,
        matchToleranceDays
      });
      if (cls.matchedCompletedId) {
        if (usedCompleted.has(cls.matchedCompletedId)) {
          const re = classify(date, options.today, [], rule.examType, {
            dueWindowDays,
            defaultGraceDays,
            matchToleranceDays
          });
          occurrences.push({ examType: rule.examType, dueDate: date, status: re.status, phaseLabel: label });
          continue;
        }
        usedCompleted.add(cls.matchedCompletedId);
      }
      occurrences.push({
        examType: rule.examType,
        dueDate: date,
        status: cls.status,
        matchedCompletedId: cls.matchedCompletedId,
        phaseLabel: label
      });
    }
  }
  occurrences.sort(
    (a, b) => compareDates(a.dueDate, b.dueDate) || a.examType.localeCompare(b.examType)
  );
  return occurrences;
}
function defaultCadenceRules() {
  return [
    {
      examType: "nfs",
      enabled: true,
      phases: [
        { fromMonth: 0, toMonth: 1, everyWeeks: 1, label: "weekly" },
        { fromMonth: 1, toMonth: 3, everyWeeks: 2, label: "fortnightly" },
        { fromMonth: 3, toMonth: 12, everyMonths: 1, label: "monthly" }
      ]
    },
    {
      examType: "weight_dose_review",
      enabled: true,
      phases: [
        { fromMonth: 0, toMonth: 1, everyWeeks: 1, label: "weekly" },
        { fromMonth: 1, toMonth: 3, everyWeeks: 2, label: "fortnightly" },
        { fromMonth: 3, toMonth: 12, everyMonths: 1, label: "monthly" }
      ]
    },
    {
      examType: "dilated_fundus",
      enabled: true,
      phases: [
        { fromMonth: 0, atMonths: [0], label: "initial" },
        { fromMonth: 1, toMonth: 12, everyMonths: 3, label: "every 2-3 months" }
      ]
    },
    {
      examType: "etf",
      enabled: true,
      phases: [
        { fromMonth: 0, atMonths: [0], label: "initial" },
        { fromMonth: 3, toMonth: 12, everyMonths: 3, label: "every 3-6 months" }
      ]
    },
    {
      examType: "baer_pea",
      enabled: true,
      phases: [{ fromMonth: 0, atMonths: [0, 6, 12], label: "initial / 6mo / 12mo" }]
    },
    {
      examType: "pediatric_consult",
      enabled: true,
      phases: [
        { fromMonth: 0, toMonth: 3, everyWeeks: 2, label: "each visit" },
        { fromMonth: 3, toMonth: 12, everyMonths: 1, label: "monthly" }
      ]
    }
  ];
}

// src/dose/dose-math.ts
function calculateDose(input) {
  const warnings = [];
  const weightGrams = dec(input.weightGrams);
  if (weightGrams.lte(0)) {
    warnings.push({ code: "zero_or_negative", messageKey: "dose.warn.zeroWeight" });
  }
  const weightKg = gramsToKg(weightGrams);
  const mgPerKg = dec(input.formula.mgPerKg);
  let rawDoseMg;
  if (input.formula.perDay) {
    const dosesPerDay = input.formula.dosesPerDay && input.formula.dosesPerDay > 0 ? dec(input.formula.dosesPerDay) : dec(1);
    rawDoseMg = mgPerKg.times(weightKg).div(dosesPerDay);
  } else {
    rawDoseMg = mgPerKg.times(weightKg);
  }
  let boundedDoseMg = rawDoseMg;
  if (input.formula.minDoseMg) {
    const minD = dec(input.formula.minDoseMg);
    if (boundedDoseMg.lt(minD)) {
      boundedDoseMg = minD;
      warnings.push({
        code: "below_min",
        messageKey: "dose.warn.belowMin",
        params: { min: toStorageString(minD) }
      });
    }
  }
  if (input.formula.maxDoseMg) {
    const maxD = dec(input.formula.maxDoseMg);
    if (boundedDoseMg.gt(maxD)) {
      boundedDoseMg = maxD;
      warnings.push({
        code: "above_max",
        messageKey: "dose.warn.aboveMax",
        params: { max: toStorageString(maxD) }
      });
    }
  }
  const increment = dec(
    input.measurableIncrement ?? DEFAULT_MEASURABLE_INCREMENT[input.form] ?? "0.01"
  );
  const direction = input.roundingDirection ?? "nearest";
  let roundedAmount;
  let roundedUnit;
  let rawVolumeMl;
  let roundingReason;
  const isLiquid = input.form === "suspension" || input.form === "compounded_liquid" || input.form === "drops";
  if (isLiquid) {
    if (!input.concentrationMgPerMl) {
      warnings.push({ code: "no_concentration", messageKey: "dose.warn.noConcentration" });
      roundedAmount = boundedDoseMg.toDecimalPlaces(3);
      roundedUnit = "mg";
      roundingReason = `No concentration provided for a liquid form; reporting ${toStorageString(
        roundedAmount
      )} mg (volume cannot be computed). Clinician must supply mg/mL.`;
    } else {
      const conc = dec(input.concentrationMgPerMl);
      rawVolumeMl = boundedDoseMg.div(conc);
      roundedAmount = roundToIncrement(rawVolumeMl, increment, direction);
      roundedUnit = "mL";
      roundingReason = `Volume = ${toStorageString(boundedDoseMg)} mg \xF7 ${toStorageString(
        conc
      )} mg/mL = ${toStorageString(rawVolumeMl)} mL, rounded ${direction} to nearest ${toStorageString(
        increment
      )} mL \u2192 ${toStorageString(roundedAmount)} mL.`;
    }
  } else if (input.form === "tablet") {
    roundedAmount = roundToIncrement(boundedDoseMg, increment, direction);
    roundedUnit = "mg";
    roundingReason = `Rounded ${direction} to nearest ${toStorageString(
      increment
    )} mg \u2192 ${toStorageString(roundedAmount)} mg.`;
  } else {
    roundedAmount = roundToIncrement(boundedDoseMg, increment, direction);
    roundedUnit = "mg";
    roundingReason = `Rounded ${direction} to nearest ${toStorageString(
      increment
    )} \u2192 ${toStorageString(roundedAmount)}.`;
  }
  if (!isLiquid || input.concentrationMgPerMl) {
    const referenceMg = isLiquid && rawVolumeMl ? rawVolumeMl : boundedDoseMg;
    if (referenceMg.gt(0)) {
      const delta = roundedAmount.minus(referenceMg).abs().div(referenceMg);
      if (delta.gt("0.2")) {
        warnings.push({
          code: "large_rounding_delta",
          messageKey: "dose.warn.largeRounding",
          params: { pct: delta.times(100).toDecimalPlaces(1).toString() }
        });
      }
    }
  }
  const evalDate = input.evaluationDate;
  const staleDays = input.staleWeightDays ?? DEFAULT_STALE_WEIGHT_DAYS;
  let weightStale = false;
  if (evalDate) {
    const age = diffDays(input.weightDate, evalDate);
    if (age > staleDays) {
      weightStale = true;
      warnings.push({
        code: "stale_weight",
        messageKey: "dose.warn.staleWeight",
        params: { days: String(age) }
      });
    }
  }
  return {
    concept: input.concept,
    formulaVersionId: input.formulaVersionId,
    weightGrams: toStorageString(weightGrams),
    weightKg: toStorageString(weightKg),
    weightDate: input.weightDate,
    rawDoseMg: toStorageString(rawDoseMg),
    boundedDoseMg: toStorageString(boundedDoseMg),
    ...rawVolumeMl ? { rawVolumeMl: toStorageString(rawVolumeMl) } : {},
    roundedAmount: toStorageString(roundedAmount),
    roundedUnit,
    roundingReason,
    warnings,
    weightStale,
    isDraft: true
  };
}

// src/labs/reference.ts
function isNumericValue(s) {
  if (s == null) return false;
  const t2 = String(s).trim();
  return t2 !== "" && /^-?\d+(\.\d+)?$/.test(t2);
}
function compareAnalyte(a) {
  const hasLow = a.referenceLow != null && a.referenceLow !== "";
  const hasHigh = a.referenceHigh != null && a.referenceHigh !== "";
  if (!hasLow && !hasHigh) return { outOfRange: null, position: "unknown" };
  if (!isNumericValue(a.value)) return { outOfRange: null, position: "unknown" };
  const v = dec(a.value);
  if (hasLow && isNumericValue(a.referenceLow) && v.lt(dec(a.referenceLow))) return { outOfRange: true, position: "low" };
  if (hasHigh && isNumericValue(a.referenceHigh) && v.gt(dec(a.referenceHigh))) return { outOfRange: true, position: "high" };
  return { outOfRange: false, position: "in" };
}
function evaluateComparator(value, valueUnit, comparator, threshold, thresholdUnit, referenceLow, referenceHigh) {
  if (!isNumericValue(value) || !isNumericValue(threshold)) return null;
  let v = dec(value);
  if (valueUnit !== thresholdUnit) {
    if (!areUnitsCompatible(valueUnit, thresholdUnit)) return null;
    v = convertAnalyte(value, valueUnit, thresholdUnit);
  }
  const t2 = dec(threshold);
  switch (comparator) {
    case "lt":
      return v.lt(t2);
    case "lte":
      return v.lte(t2);
    case "gt":
      return v.gt(t2);
    case "gte":
      return v.gte(t2);
    case "eq":
      return v.eq(t2);
    case "neq":
      return !v.eq(t2);
    case "out_of_range": {
      const orig = dec(value);
      if (referenceLow != null && referenceLow !== "" && orig.lt(dec(referenceLow))) return true;
      if (referenceHigh != null && referenceHigh !== "" && orig.gt(dec(referenceHigh))) return true;
      return false;
    }
    default:
      return null;
  }
}
function trendDirection(seriesOldestFirst) {
  if (seriesOldestFirst.length < 2) return "unknown";
  const last = dec(seriesOldestFirst[seriesOldestFirst.length - 1]);
  const prev = dec(seriesOldestFirst[seriesOldestFirst.length - 2]);
  if (last.lt(prev)) return "down";
  if (last.gt(prev)) return "up";
  return "flat";
}
function consecutiveDeclines(seriesOldestFirst) {
  let count = 0;
  for (let i = seriesOldestFirst.length - 1; i > 0; i--) {
    if (dec(seriesOldestFirst[i]).lt(dec(seriesOldestFirst[i - 1]))) count++;
    else break;
  }
  return count;
}

// src/labs/lab-status.ts
var RESOLVED_STATUSES = /* @__PURE__ */ new Set(["received", "reviewed", "rejected"]);
function evaluateLabStatus(input) {
  const pendingDays = input.pendingResultDays ?? DEFAULT_PENDING_RESULT_DAYS;
  const redrawNeeded = input.status === "redraw_needed";
  const resultIn = !RESOLVED_STATUSES.has(input.status);
  let daysPending = null;
  let pendingTooLong = false;
  if (resultIn && (input.status === "pending" || input.status === "drawn" || input.status === "ordered")) {
    daysPending = diffDays(input.sampleDate, input.evaluationDate);
    pendingTooLong = daysPending >= pendingDays;
  }
  return { pendingTooLong, redrawNeeded, daysPending };
}
function flagAnalytes(analytes) {
  return analytes.map((a) => {
    const cmp = compareAnalyte(a);
    return {
      ...a,
      outOfRange: cmp.outOfRange,
      position: cmp.position
    };
  });
}

// src/rules/rule-engine.ts
function sortLabsAsc(labs) {
  return [...labs].sort((a, b) => a.sampleDate < b.sampleDate ? -1 : a.sampleDate > b.sampleDate ? 1 : 0);
}
function latestAnalyte(labs, key) {
  const asc = sortLabsAsc(labs);
  for (let i = asc.length - 1; i >= 0; i--) {
    const found = asc[i].analytes.find((a) => a.key === key);
    if (found) return { lab: asc[i], analyte: found };
  }
  return null;
}
function analyteSeries(labs, key) {
  return sortLabsAsc(labs).map((l) => l.analytes.find((a) => a.key === key)?.value).filter((v) => v != null && isNumericValue(v));
}
function escalates(severity) {
  return severity === "urgent" || severity === "critical";
}
function evaluateRules(rules, context) {
  const flags = [];
  for (const rule of rules) {
    if (!rule.enabled) continue;
    const base = {
      ruleId: rule.id,
      type: rule.type,
      severity: rule.severity,
      recipients: rule.recipients,
      messageKey: rule.messageKey,
      actionKey: rule.actionKey,
      ruleVersion: rule.ruleVersion
    };
    switch (rule.type) {
      case "threshold": {
        if (!rule.analyteKey || !rule.comparator || rule.threshold == null || !rule.thresholdUnit) break;
        const found = latestAnalyte(context.labs, rule.analyteKey);
        if (!found) break;
        const breached = evaluateComparator(
          found.analyte.value,
          found.analyte.unit,
          rule.comparator,
          rule.threshold,
          rule.thresholdUnit,
          found.analyte.referenceLow,
          found.analyte.referenceHigh
        );
        if (breached === true) {
          flags.push({
            ...base,
            context: {
              analyte: rule.analyteKey,
              value: found.analyte.value,
              unit: found.analyte.unit,
              threshold: rule.threshold,
              thresholdUnit: rule.thresholdUnit,
              sampleDate: found.lab.sampleDate
            },
            requiresDoctorReview: escalates(rule.severity) || rule.recipients !== "parent"
          });
        }
        break;
      }
      case "trend": {
        if (!rule.analyteKey) break;
        const series = analyteSeries(context.labs, rule.analyteKey);
        const need = rule.declineCount && rule.declineCount > 0 ? rule.declineCount : 2;
        const declines = consecutiveDeclines(series);
        if (declines >= need) {
          flags.push({
            ...base,
            context: {
              analyte: rule.analyteKey,
              consecutiveDeclines: String(declines),
              points: String(series.length)
            },
            requiresDoctorReview: escalates(rule.severity) || rule.recipients !== "parent"
          });
        }
        break;
      }
      case "due_date":
      case "overdue": {
        if (!rule.examType) break;
        const grace = rule.overdueGraceDays ?? 0;
        for (const occ of context.surveillance) {
          if (occ.examType !== rule.examType) continue;
          if (rule.type === "overdue" && occ.status === "overdue") {
            const overdueDays = -diffDays(context.evaluationDate, occ.dueDate);
            if (overdueDays > grace) {
              flags.push({
                ...base,
                context: { examType: occ.examType, dueDate: occ.dueDate, overdueDays: String(overdueDays) },
                requiresDoctorReview: escalates(rule.severity)
              });
            }
          } else if (rule.type === "due_date" && occ.status === "due") {
            flags.push({
              ...base,
              context: { examType: occ.examType, dueDate: occ.dueDate },
              requiresDoctorReview: false
            });
          }
        }
        break;
      }
      case "missing_data": {
        for (const lab of context.pendingLabs) {
          const status = evaluateLabStatus({
            ...lab,
            pendingResultDays: rule.pendingResultDays ?? lab.pendingResultDays
          });
          if (status.pendingTooLong || status.redrawNeeded) {
            flags.push({
              ...base,
              context: {
                reason: status.redrawNeeded ? "redraw_needed" : "pending_too_long",
                daysPending: status.daysPending != null ? String(status.daysPending) : "unknown",
                sampleDate: lab.sampleDate
              },
              requiresDoctorReview: escalates(rule.severity)
            });
          }
        }
        break;
      }
      case "symptom_triggered": {
        if (!rule.symptomKey) break;
        const hit = context.symptoms.find((s) => s.key === rule.symptomKey && s.present);
        if (hit) {
          flags.push({
            ...base,
            context: { symptom: rule.symptomKey, date: hit.date },
            requiresDoctorReview: escalates(rule.severity)
          });
        }
        break;
      }
      case "custom": {
        if (rule.messageKey === "rule.g6pd.unknown") {
          const s = context.g6pdStatus;
          if (s === "unknown" || s === "pending") {
            flags.push({
              ...base,
              context: { g6pdStatus: s },
              requiresDoctorReview: false
            });
          }
        }
        break;
      }
    }
  }
  return flags;
}
function staleWeightFlag(context, rule) {
  if (!context.latestWeightDate) return null;
  const staleDays = context.staleWeightDays ?? rule.pendingResultDays ?? 14;
  const age = diffDays(context.latestWeightDate, context.evaluationDate);
  if (age <= staleDays) return null;
  return {
    ruleId: rule.id,
    type: rule.type,
    severity: rule.severity,
    recipients: rule.recipients,
    messageKey: rule.messageKey,
    actionKey: rule.actionKey,
    ruleVersion: rule.ruleVersion,
    context: { weightAgeDays: String(age) },
    requiresDoctorReview: true
  };
}

// src/adherence/adherence.ts
function pct(numerator, denominator) {
  if (denominator === 0) return 0;
  return dec(numerator).div(denominator).times(100).toDecimalPlaces(1).toNumber();
}
function computeAdherence(records) {
  const counts = {
    totalScheduled: 0,
    taken: 0,
    late: 0,
    missed: 0,
    refused: 0,
    vomited: 0,
    partial: 0
  };
  const byConceptMap = /* @__PURE__ */ new Map();
  for (const r of records) {
    const status = r.status;
    if (status === "not_applicable") continue;
    counts.totalScheduled++;
    let cc = byConceptMap.get(r.concept);
    if (!cc) {
      cc = {
        concept: r.concept,
        scheduled: 0,
        taken: 0,
        late: 0,
        missed: 0,
        adherencePct: 0
      };
      byConceptMap.set(r.concept, cc);
    }
    cc.scheduled++;
    switch (status) {
      case "taken":
        counts.taken++;
        cc.taken++;
        break;
      case "late":
        counts.late++;
        cc.late++;
        break;
      case "partial":
        counts.partial++;
        cc.taken++;
        break;
      case "missed":
        counts.missed++;
        cc.missed++;
        break;
      case "refused":
        counts.refused++;
        cc.missed++;
        break;
      case "vomited_after_dose":
        counts.vomited++;
        cc.missed++;
        break;
    }
  }
  const adherent = counts.taken + counts.late + counts.partial;
  const byConcept = {};
  for (const [concept, cc] of byConceptMap) {
    cc.adherencePct = pct(cc.taken + cc.late, cc.scheduled);
    byConcept[concept] = cc;
  }
  return {
    ...counts,
    adherencePct: pct(adherent, counts.totalScheduled),
    byConcept
  };
}
function slotKey(orderId, date, time) {
  return `${orderId}|${date}|${time}`;
}
function detectMissedDoses(input) {
  const grace = input.graceMinutes ?? 0;
  const missed = [];
  for (const slot of input.scheduled) {
    const key = slotKey(slot.orderId, slot.date, slot.time);
    if (input.logged.has(key)) continue;
    if (slot.date < input.nowDate) {
      missed.push(slot);
      continue;
    }
    if (slot.date === input.nowDate) {
      const [h, m] = slot.time.split(":").map((x) => parseInt(x, 10));
      const slotMinutes = (h ?? 0) * 60 + (m ?? 0);
      if (input.nowMinutes > slotMinutes + grace) missed.push(slot);
    }
  }
  return missed;
}
var adherentStatuses = ADHERENT_STATUSES;

// src/i18n/fr.ts
var fr = {
  // --- App / disclaimers (Safety §8) ---
  "app.name": "ToxoFollow",
  "app.tagline": "Suivi de la toxoplasmose cong\xE9nitale",
  "disclaimer.short": "Cet outil assiste mais ne remplace pas le jugement m\xE9dical. En cas d\u2019urgence, contactez le m\xE9decin ou les services d\u2019urgence.",
  "disclaimer.firstLaunch": "ToxoFollow est un outil de suivi et d\u2019aide \xE0 la d\xE9cision. Ce n\u2019est pas un dispositif m\xE9dical et il ne fournit pas d\u2019avis m\xE9dical. Il ne diagnostique pas, ne prescrit pas et ne modifie jamais un traitement de lui-m\xEAme. Les rappels sont des alertes de confort, et non une garantie d\u2019horaire de prise. En cas d\u2019urgence, contactez votre m\xE9decin ou les services d\u2019urgence.",
  "disclaimer.report": "Document g\xE9n\xE9r\xE9 par ToxoFollow. Soutient mais ne remplace pas le jugement m\xE9dical. Les doses indiqu\xE9es doivent \xEAtre valid\xE9es par un m\xE9decin.",
  "banner.notValidated": "Configuration clinique non encore valid\xE9e m\xE9dicalement. Faites r\xE9viser et signer la configuration par le p\xE9diatre.",
  // --- Roles ---
  "role.caregiver": "Parent / aidant",
  "role.clinician": "M\xE9decin",
  "role.owner_admin": "Administrateur",
  // --- Medication concepts (generic) ---
  "concept.pyrimethamine": "Pyrim\xE9thamine",
  "concept.sulfadiazine": "Sulfadiazine",
  "concept.folinic_acid": "Acide folinique (Lederfoline)",
  "concept.folic_acid": "Acide folique (AcFol)",
  "concept.vitamin_d": "Vitamine D",
  "concept.supplemental_hydration": "Hydratation suppl\xE9mentaire",
  "concept.antibiotic": "Antibiotique",
  "concept.custom": "M\xE9dicament personnalis\xE9",
  "meds.adjunct": "Adjuvant / temporaire",
  // --- Administration statuses ---
  "admin.taken": "Donn\xE9",
  "admin.late": "Donn\xE9 en retard",
  "admin.missed": "Oubli\xE9",
  "admin.vomited_after_dose": "Vomi apr\xE8s la dose",
  "admin.partial": "Partiel",
  "admin.refused": "Refus\xE9",
  "admin.held_by_doctor": "Suspendu par le m\xE9decin",
  "admin.not_applicable": "N/A",
  "admin.unknown": "Inconnu",
  // --- Lab statuses ---
  "lab.ordered": "Prescrit",
  "lab.drawn": "Pr\xE9lev\xE9",
  "lab.pending": "En attente de r\xE9sultat",
  "lab.received": "R\xE9sultat re\xE7u",
  "lab.redraw_needed": "Nouveau pr\xE9l\xE8vement n\xE9cessaire",
  "lab.rejected": "Rejet\xE9",
  "lab.reviewed": "Revu par le m\xE9decin",
  // --- Lab interpretations ---
  "interp.title": "Interpr\xE9tation",
  "interp.no_change": "Aucun changement",
  "interp.repeat": "\xC0 r\xE9p\xE9ter",
  "interp.dose_change": "Changement de dose",
  "interp.temporary_hold": "Suspension temporaire",
  "interp.stop": "Arr\xEAt",
  "interp.consult_hematology": "Avis h\xE9matologie",
  "interp.other": "Autre",
  // --- Analytes ---
  "analyte.hemoglobin": "Hb (H\xE9moglobine)",
  "analyte.wbc": "GB (Globules blancs)",
  "analyte.neutrophils_abs": "PNN (absolu)",
  "analyte.neutrophils_pct": "PNN (%)",
  "analyte.platelets": "Plaquettes",
  "analyte.lymphocytes_pct": "Lymphocytes (%)",
  "analyte.lymphocytes_abs": "Lymphocytes (absolu)",
  "analyte.reticulocytes": "R\xE9ticulocytes",
  "analyte.bilirubin": "Bilirubine",
  "analyte.alt": "ALAT",
  "analyte.ast": "ASAT",
  "analyte.creatinine": "Cr\xE9atinine",
  "analyte.urea": "Ur\xE9e",
  "analyte.crp": "CRP",
  "analyte.custom": "Personnalis\xE9",
  // --- Serology ---
  "serology.igg": "IgG",
  "serology.igm": "IgM",
  "serology.iga": "IgA",
  "serology.pcr": "PCR",
  // --- Exam types ---
  "exam.nfs": "NFS (CBC)",
  "exam.weight_dose_review": "Pes\xE9e et r\xE9vision de dose",
  "exam.dilated_fundus": "Fond d\u2019\u0153il dilat\xE9",
  "exam.etf": "\xC9chographie transfontanellaire (ETF)",
  "exam.mri": "IRM",
  "exam.baer_pea": "PEA (audition)",
  "exam.pediatric_consult": "Consultation p\xE9diatre",
  "exam.serology": "S\xE9rologie",
  "exam.other": "Autre",
  // --- Severity ---
  "severity.info": "Information",
  "severity.warning": "Avertissement",
  "severity.urgent": "Urgent",
  "severity.critical": "Critique",
  // --- Dose calculator warnings ---
  "dose.warn.staleWeight": "Poids ancien ({days} jours) \u2014 une r\xE9vision de dose peut \xEAtre n\xE9cessaire.",
  "dose.warn.belowMin": "Dose calcul\xE9e sous le minimum configur\xE9 ({min} mg) ; minimum appliqu\xE9.",
  "dose.warn.aboveMax": "Dose calcul\xE9e au-dessus du maximum configur\xE9 ({max} mg) ; maximum appliqu\xE9.",
  "dose.warn.noConcentration": "Concentration (mg/mL) manquante pour une forme liquide ; volume non calculable.",
  "dose.warn.largeRounding": "Arrondi important ({pct}%) par rapport \xE0 la dose calcul\xE9e \u2014 \xE0 v\xE9rifier.",
  "dose.warn.zeroWeight": "Poids nul ou n\xE9gatif \u2014 v\xE9rifiez la saisie.",
  "dose.draftNotice": "Dose calcul\xE9e : brouillon \xE0 valider par le m\xE9decin. Non active tant que non approuv\xE9e.",
  // --- Rule messages (defaults; clinician-configurable) ---
  "rule.pnn.belowThreshold": "PNN ({value} {unit}) sous le seuil configur\xE9 ({threshold} {thresholdUnit}).",
  "rule.hb.low": "H\xE9moglobine ({value} {unit}) sous le niveau d\u2019alerte.",
  "rule.platelets.low": "Plaquettes ({value} {unit}) sous le niveau d\u2019alerte.",
  "rule.trend.decline": "Baisse soutenue d\xE9tect\xE9e ({consecutiveDeclines} mesures cons\xE9cutives).",
  "rule.lab.pendingTooLong": "R\xE9sultat en attente depuis {daysPending} jours \u2014 relancer le laboratoire.",
  "rule.lab.redrawNeeded": "Nouveau pr\xE9l\xE8vement n\xE9cessaire.",
  "rule.exam.due": "Examen {examType} \xE0 pr\xE9voir (pr\xE9vu le {dueDate}).",
  "rule.exam.overdue": "Examen {examType} en retard de {overdueDays} jours.",
  "rule.symptom.present": "Sympt\xF4me signal\xE9 : {symptom} ({date}).",
  "rule.g6pd.unknown": "Statut G6PD non renseign\xE9 \u2014 \xE0 demander au m\xE9decin.",
  "rule.weight.stale": "Poids ancien ({weightAgeDays} jours) \u2014 r\xE9vision de dose \xE0 envisager.",
  // --- Default doctor-configured actions (Safety §4) ---
  "action.contactPediatrician": "Contactez le p\xE9diatre traitant.",
  "action.scheduleLab": "Planifiez le prochain pr\xE9l\xE8vement.",
  "action.followUpLab": "Relancez le laboratoire pour le r\xE9sultat.",
  "action.bookExam": "Prenez rendez-vous pour cet examen.",
  "action.askDoctor": "Posez la question au m\xE9decin lors de la prochaine consultation.",
  "action.doctorReview": "Une r\xE9vision m\xE9dicale est n\xE9cessaire.",
  // --- Weekly sheet / daily check-in ---
  "weekly.title": "Suivi hebdomadaire",
  "weekly.weekOf": "Semaine du",
  "weekly.weight": "Poids du b\xE9b\xE9 (g)",
  "weekly.observations": "Observations de la semaine",
  "weekly.questions": "Pour la prochaine consultation",
  "checkin.temperature": "Temp\xE9rature max",
  "checkin.wetDiapers": "Couches mouill\xE9es (#)",
  "checkin.stools": "Selles (#)",
  "checkin.feeding": "Alimentation",
  "checkin.sleep": "Sommeil / humeur",
  "checkin.vomiting": "Vomissements",
  "checkin.rash": "\xC9ruption",
  "checkin.pallor": "P\xE2leur",
  "checkin.bruising": "Ecchymoses",
  "checkin.hydration": "Hydratation",
  "checkin.appetite": "App\xE9tit",
  // --- Symptoms ---
  "symptom.fever": "Fi\xE8vre",
  "symptom.pallor": "P\xE2leur",
  "symptom.bruising": "Ecchymoses",
  "symptom.rash": "\xC9ruption",
  "symptom.vomiting": "Vomissements",
  "symptom.vomiting_after_dose": "Vomissement apr\xE8s la dose",
  "symptom.poor_feeding": "Mauvaise alimentation",
  "symptom.low_wet_diapers": "Peu de couches mouill\xE9es",
  "symptom.dehydration_concern": "Suspicion de d\xE9shydratation",
  "symptom.lethargy": "L\xE9thargie",
  "symptom.irritability": "Irritabilit\xE9",
  "symptom.other": "Autre",
  // --- Growth / units ---
  "growth.weight": "Poids",
  "growth.headCircumference": "P\xE9rim\xE8tre cr\xE2nien (PC)",
  "weight.source.parent": "Parent",
  "weight.source.clinic": "Clinique",
  "weight.source.hospital": "H\xF4pital",
  "weight.source.pharmacy": "Pharmacie",
  "weight.source.imported": "Import\xE9",
  "weight.unverified": "Poids non v\xE9rifi\xE9",
  "weight.verified": "Poids v\xE9rifi\xE9",
  // --- G6PD / baseline ---
  "g6pd.unknown": "Inconnu",
  "g6pd.pending": "En attente",
  "g6pd.normal": "Normal",
  "g6pd.abnormal": "Anormal",
  "g6pd.not_applicable": "Non applicable",
  // --- Common UI ---
  "common.save": "Enregistrer",
  "common.cancel": "Annuler",
  "common.confirm": "Confirmer",
  "common.approve": "Approuver",
  "common.reject": "Rejeter",
  "common.sign": "Signer",
  "common.today": "Aujourd\u2019hui",
  "common.loading": "Chargement\u2026",
  "common.error": "Une erreur est survenue",
  "common.offline": "Hors ligne",
  "common.synced": "Synchronis\xE9",
  "common.pendingSync": "En attente de synchronisation",
  // --- Order approval status ---
  "approval.draft": "Brouillon",
  "approval.pending_review": "En attente de revue",
  "approval.approved": "Approuv\xE9",
  "approval.rejected": "Rejet\xE9",
  "approval.suspended": "Suspendu",
  "approval.discontinued": "Arr\xEAt\xE9",
  // --- Surveillance occurrence status ---
  "surv.upcoming": "\xC0 venir",
  "surv.due": "\xC0 pr\xE9voir",
  "surv.overdue": "En retard",
  "surv.done": "Fait",
  // --- Frequency ---
  "freq.once_daily": "1\xD7/jour",
  "freq.twice_daily": "2\xD7/jour",
  "freq.three_times_daily": "3\xD7/jour",
  "freq.weekly": "Hebdomadaire",
  "freq.three_times_weekly": "3\xD7/semaine",
  "freq.every_other_day": "Un jour sur deux",
  "freq.every_x_hours": "Toutes les X heures",
  "freq.specific_weekdays": "Jours pr\xE9cis",
  "freq.custom": "Personnalis\xE9",
  // --- Formes galéniques ---
  "form.tablet": "Comprim\xE9",
  "form.suspension": "Suspension",
  "form.compounded_liquid": "Pr\xE9paration liquide",
  "form.capsule": "G\xE9lule",
  "form.drops": "Gouttes",
  "form.powder": "Poudre",
  "form.other": "Autre",
  // --- Écran Médicaments ---
  "med.title": "M\xE9dicaments",
  "med.new": "Nouveau m\xE9dicament",
  "med.concept": "M\xE9dicament",
  "med.reason": "Motif",
  "med.suspend": "Suspendre",
  "med.discontinue": "Arr\xEAter",
  "med.current": "en cours",
  "med.folinic": "folinique",
  "med.folic": "folique",
  "med.folateWarning": "Ne jamais confondre acide folinique et acide folique",
  "med.draftNotice": "Les nouvelles prescriptions sont enregistr\xE9es en brouillon et n\xE9cessitent une approbation explicite avant d\u2019\xEAtre actives.",
  "med.brandName": "Marque / nom local",
  "med.form": "Forme",
  "med.concentration": "Concentration (mg/mL)",
  "med.doseAmount": "Dose",
  "med.doseUnit": "Unit\xE9 de dose",
  "med.frequency": "Fr\xE9quence",
  "med.times": "Heures (HH:mm, s\xE9par\xE9es par une virgule)",
  "med.startDate": "Date de d\xE9but",
  "med.parentInstructions": "Instructions pour le parent",
  "med.doseHistory": "Historique des doses",
  "med.dose": "Dose",
  "med.signed": "Sign\xE9",
  "med.reasonPrompt": "Motif de l\u2019arr\xEAt ?",
  // --- Common UI (additional) ---
  "common.delete": "Supprimer",
  "common.download": "T\xE9l\xE9charger",
  "common.edit": "Modifier",
  "common.from": "Du",
  "common.to": "Au",
  "common.date": "Date",
  "common.status": "Statut",
  "common.source": "Source",
  "common.size": "Taille",
  "common.value": "Valeur",
  "common.unit": "Unit\xE9",
  "common.section": "Section",
  "common.summary": "R\xE9sum\xE9",
  "common.signed": "Sign\xE9",
  "common.next": "Prochain",
  "common.flags": "Signalements",
  // --- Authentification (écran de connexion) ---
  "auth.email": "E-mail",
  "auth.password": "Mot de passe",
  "auth.signIn": "Se connecter",
  "auth.mfaCode": "Code MFA (TOTP)",
  "auth.mfaRequired": "Code MFA requis",
  "auth.invalidCredentials": "Identifiants invalides",
  "auth.parent": "Parent",
  "auth.clinicianAdmin": "M\xE9decin / Admin",
  "auth.parentAccess": "Acc\xE8s parent par num\xE9ro de t\xE9l\xE9phone",
  "auth.loginBySms": "Code SMS",
  "auth.loginByPassword": "E-mail + mot de passe",
  "auth.smsAccess": "Connexion par code SMS \u2014 pour les parents et le personnel dont le num\xE9ro est enregistr\xE9.",
  "auth.phone": "T\xE9l\xE9phone",
  "auth.codeReceived": "Code re\xE7u",
  "auth.verifyCode": "V\xE9rifier le code",
  "auth.sendCode": "Envoyer le code",
  "auth.changeNumber": "Changer de num\xE9ro",
  "auth.codeSent": "Si ce num\xE9ro est autoris\xE9, un code a \xE9t\xE9 envoy\xE9.",
  "auth.codeSendFailed": "\xC9chec de l\u2019envoi du code",
  "auth.tooManyAttempts": "Trop de tentatives \u2014 patientez une minute puis r\xE9essayez.",
  "auth.invalidCode": "Code invalide ou expir\xE9",
  "auth.disclaimer": "Cet outil assiste mais ne remplace pas le jugement m\xE9dical. En cas d\u2019urgence, contactez le m\xE9decin.",
  // --- Écran Révision de dose ---
  "dose.title": "R\xE9vision de dose",
  "dose.calculator": "Calculateur de dose",
  "dose.reviewQueue": "File d\u2019attente \u2014 brouillons",
  "dose.ordersPendingReview": "Ordres en attente de revue",
  "dose.weightDate": "Date de pes\xE9e",
  "dose.mgPerKg": "mg/kg",
  "dose.dosesPerDay": "Doses/jour",
  "dose.measurableIncrement": "Incr\xE9ment mesurable",
  "dose.minDoseMg": "Dose min (mg)",
  "dose.maxDoseMg": "Dose max (mg)",
  "dose.perDayHint": "mg/kg est une quantit\xE9 journali\xE8re (r\xE9partie sur les doses/jour)",
  "dose.calculate": "Calculer",
  "dose.raw": "Brut",
  "dose.bounded": "Born\xE9",
  "dose.rounded": "Arrondi",
  "dose.toOrder": "Vers un ordre",
  "dose.recordOnly": "\u2014 (enregistrement seul, aucun ordre)",
  "dose.editedAmount": "Dose modifi\xE9e",
  "dose.editedUnit": "Unit\xE9 modifi\xE9e",
  "dose.reasonRequired": "Motif (requis)",
  // Révision de dose — calculateur guidé
  "dose.calcSubtitle": "Calculez une dose (brouillon). Rien n\u2019est appliqu\xE9 tant que vous ne l\u2019approuvez pas ci-dessous.",
  "dose.queueSubtitle": "Brouillons calcul\xE9s en attente de votre approbation.",
  "dose.ordersSubtitle": "M\xE9dicaments approuv\xE9s dont la dose reste \xE0 d\xE9finir.",
  "dose.step1": "1 \xB7 Poids du b\xE9b\xE9",
  "dose.step2": "2 \xB7 M\xE9dicament",
  "dose.step3": "3 \xB7 R\xE8gle de dosage",
  "dose.amountBasis": "Le mg/kg saisi correspond \xE0\u2026",
  "dose.perDose": "Par prise",
  "dose.perDay": "Par jour",
  "dose.perDoseHint": "Chaque prise = mg/kg \xD7 poids.",
  "dose.advanced": "Avanc\xE9 \u2014 bornes & arrondi",
  "dose.incrementHint": "Plus petit pas mesurable (ex. graduation 0,1 mL, 1/4 de comprim\xE9).",
  "dose.solidNote": "Forme solide \u2014 dos\xE9e en mg, concentration inutile.",
  "dose.preview": "Aper\xE7u",
  "dose.perDoseShort": "prise",
  "dose.beforeRounding": "Avant arrondi",
  "dose.give": "Dose \xE0 administrer",
  // --- Écran NFS / Labos (formulaires) ---
  "lab.sampleDate": "Date de pr\xE9l\xE8vement",
  "lab.resultDate": "Date du r\xE9sultat",
  "lab.refLow": "R\xE9f. basse",
  "lab.refHigh": "R\xE9f. haute",
  "lab.sentAbroad": "Envoy\xE9 \xE0 l\u2019\xE9tranger",
  "lab.abroad": "\xE9tranger",
  "lab.flags": "Signalements",
  "lab.toVerify": "\xC0 v\xE9rifier",
  "lab.nothingToVerify": "Aucun r\xE9sultat \xE0 v\xE9rifier",
  "lab.viewScan": "Voir le scan",
  "lab.verifySign": "V\xE9rifier et signer",
  // --- Écran Sérologie (formulaire) ---
  "sero.qual": "Qual.",
  "sero.titer": "Titre",
  "sero.titerUnit": "Unit\xE9 de titre",
  "sero.qualitative": "Qualitatif",
  "sero.marker": "Marqueur",
  // --- Écran Pièces jointes ---
  "attach.title": "Pi\xE8ces jointes",
  "attach.upload": "T\xE9l\xE9verser",
  "attach.notice": "Fichiers chiffr\xE9s au repos, sans URL publique. T\xE9l\xE9chargements audit\xE9s.",
  "attach.file": "Fichier",
  "attach.kind": "Type",
  "attach.linkedType": "Type li\xE9 (facultatif)",
  "attach.linkedId": "Identifiant li\xE9 (facultatif)",
  "attach.linked": "Li\xE9",
  "attach.noFileSelected": "Aucun fichier s\xE9lectionn\xE9",
  // --- Écran RDV / Examens ---
  "appt.title": "RDV / Examens",
  "appt.importEtf": "Importer un compte rendu ETF",
  "appt.surveillanceCalendar": "Calendrier de surveillance (12 mois)",
  "appt.appointments": "Rendez-vous",
  "appt.findings": "Constatations",
  "appt.nextDate": "Date suivante",
  "appt.specialist": "Sp\xE9cialiste",
  "appt.reason": "Motif",
  "appt.examType": "Type d\u2019examen",
  "appt.signed": "sign\xE9",
  "appt.status.scheduled": "Planifi\xE9",
  "appt.status.completed": "R\xE9alis\xE9",
  "appt.status.missed": "Manqu\xE9",
  "appt.status.cancelled": "Annul\xE9",
  "appt.status.rescheduled": "Reprogramm\xE9",
  // Confirmation parent (distincte du statut clinicien)
  "apptConfirm.booked": "R\xE9serv\xE9",
  "apptConfirm.attended": "Effectu\xE9",
  "apptConfirm.missed": "Manqu\xE9",
  "appt.parentReportsAttended": "La famille indique \xAB effectu\xE9 \xBB \u2014 \xE0 v\xE9rifier",
  // --- Écran Observations / Questions ---
  "obs.title": "Observations / Questions",
  "obs.dailyCheckins": "Points quotidiens",
  "obs.symptoms": "Sympt\xF4mes",
  // --- Écran Observance ---
  "adherence.title": "Observance",
  "adherence.range": "P\xE9riode",
  "adherence.perMedication": "Par m\xE9dicament",
  // --- Écran Rapports ---
  "reports.title": "Rapports",
  "reports.exports": "Exports",
  "reports.encryptedZip": "ZIP chiffr\xE9",
  "reports.icsCalendar": "calendrier",
  "reports.pdfNotice": "Le PDF de consultation est g\xE9n\xE9r\xE9 sur le t\xE9l\xE9phone du parent (application mobile).",
  "reports.preview": "Aper\xE7u",
  "reports.patient": "Patient",
  "reports.medications": "M\xE9dicaments",
  "reports.attachments": "Pi\xE8ces jointes",
  // --- Compte / mot de passe (changement par l'utilisateur + réinit. admin) ---
  "account.title": "Mon compte",
  "account.security": "S\xE9curit\xE9",
  "account.signedInAs": "Connect\xE9 en tant que {name}",
  "auth.changePassword": "Changer le mot de passe",
  "auth.currentPassword": "Mot de passe actuel",
  "auth.newPassword": "Nouveau mot de passe",
  "auth.confirmPassword": "Confirmer le mot de passe",
  "auth.passwordMismatch": "Les mots de passe ne correspondent pas.",
  "auth.passwordTooShort": "Au moins 12 caract\xE8res.",
  "auth.passwordSame": "Le nouveau mot de passe doit \xEAtre diff\xE9rent de l\u2019actuel.",
  "auth.passwordChanged": "Mot de passe modifi\xE9.",
  "auth.currentPasswordWrong": "Mot de passe actuel incorrect.",
  // --- Admin : réinitialiser le mot de passe d'un utilisateur ---
  "admin.resetPassword": "R\xE9initialiser le mot de passe",
  "admin.resetPasswordFor": "R\xE9initialiser le mot de passe de {name}",
  "admin.newPasswordOptional": "Nouveau mot de passe (laisser vide pour en g\xE9n\xE9rer un)",
  "admin.generatedPassword": "Mot de passe temporaire (affich\xE9 une seule fois)",
  "admin.tempPasswordOnce": "Communiquez-le \xE0 l\u2019utilisateur ; il ne sera plus affich\xE9.",
  "admin.passwordReset": "Mot de passe r\xE9initialis\xE9.",
  "admin.cannotResetNoEmail": "Pas d\u2019e-mail \u2014 ajoutez-en un pour permettre le mot de passe",
  // --- Médicaments : modifier + dupliquer ---
  "med.copy": "Dupliquer",
  "med.editTitle": "Modifier le m\xE9dicament",
  "med.copyTitle": "Dupliquer le m\xE9dicament",
  "med.reapprovalNeeded": "Une modification clinique remet l\u2019ordonnance en attente d\u2019approbation.",
  "med.localName": "Nom local",
  "med.endDate": "Date de fin",
  "med.doctorNotes": "Notes du m\xE9decin",
  "med.safetyNotes": "Notes de s\xE9curit\xE9",
  "med.prescriber": "Prescripteur",
  "med.doseMgPerKg": "Dose (mg/kg)",
  // --- Notes cliniques (texte libre du médecin) ---
  "notes.title": "Notes cliniques",
  "notes.subtitle": "Notes libres du m\xE9decin (G6PD, ETF, observations).",
  "notes.add": "Ajouter une note",
  "notes.category": "Cat\xE9gorie",
  "notes.noteDate": "Date",
  "notes.text": "Note",
  "notes.placeholder": "Saisir une observation clinique\u2026",
  "notes.empty": "Aucune note clinique.",
  "notes.saved": "Note enregistr\xE9e.",
  "notes.delete": "Supprimer",
  "notes.deleted": "Note supprim\xE9e.",
  "notes.by": "par {who}",
  "notes.cat.general": "G\xE9n\xE9ral",
  "notes.cat.g6pd": "G6PD",
  "notes.cat.imaging": "Imagerie / ETF",
  "notes.cat.exam": "Examen",
  "notes.cat.lab": "Biologie",
  "notes.cat.other": "Autre",
  "notes.visibleToFamily": "Visible par la famille",
  "notes.sharedWithFamily": "Partag\xE9 avec la famille",
  "notes.privateNote": "Note priv\xE9e (m\xE9decin)",
  "notes.shareWithFamily": "Partager avec la famille",
  "notes.hideFromFamily": "Masquer \xE0 la famille",
  "notes.fromDoctor": "Notes de votre m\xE9decin",
  "notes.importEtf": "Importer un compte rendu (ETF / imagerie)",
  "notes.importEtfHint": "D\xE9posez le compte rendu ; l\u2019IA propose un r\xE9sum\xE9 que vous v\xE9rifiez avant d\u2019enregistrer.",
  "notes.aiPrivacy": "Le fichier est analys\xE9 par un service d\u2019IA (Anthropic) configur\xE9 par votre \xE9tablissement.",
  "notes.analyze": "Analyser avec l\u2019IA",
  "notes.analyzing": "Analyse en cours\u2026",
  "notes.draftReview": "Brouillon propos\xE9 \u2014 v\xE9rifiez puis enregistrez.",
  "notes.aiUnavailable": "Analyse IA indisponible (type de fichier non pris en charge ou IA d\xE9sactiv\xE9e). Saisissez le r\xE9sum\xE9 manuellement.",
  "notes.saveAsNote": "Enregistrer comme note",
  // --- Données de base du patient (G6PD, allergies) ---
  "baseline.title": "Donn\xE9es de base",
  "baseline.edit": "Modifier les donn\xE9es de base",
  "baseline.g6pd": "Statut G6PD",
  "baseline.allergies": "Allergies (s\xE9par\xE9es par des virgules)",
  "baseline.intolerances": "Intol\xE9rances (s\xE9par\xE9es par des virgules)",
  "baseline.birthDetails": "D\xE9tails de naissance",
  "baseline.notes": "Notes de base",
  "baseline.saved": "Donn\xE9es de base enregistr\xE9es.",
  // --- Navigation / coque ---
  "nav.settings": "Compte",
  "nav.notes": "Notes cliniques",
  "nav.openMenu": "Ouvrir le menu",
  "nav.closeMenu": "Fermer le menu",
  // --- Navigation par jour (vue parent) ---
  "day.title": "Journ\xE9e",
  "day.prev": "Jour pr\xE9c\xE9dent",
  "day.next": "Jour suivant",
  "day.today": "Aujourd\u2019hui",
  "day.backdated": "Saisie pour le {date} (jour pass\xE9)",
  "day.noEntries": "Rien n\u2019a \xE9t\xE9 enregistr\xE9 pour ce jour.",
  "day.editEntries": "Modifier les saisies de ce jour",
  "day.summaryCheckin": "Relev\xE9 du jour",
  "day.summaryGrowth": "Poids / p\xE9rim\xE8tre cr\xE2nien",
  "day.summaryObservations": "Observations",
  // --- Historique des médicaments donnés ---
  "history.from": "Du",
  "history.to": "Au",
  "history.title": "Historique des m\xE9dicaments",
  "history.subtitle": "Toutes les doses enregistr\xE9es, de la plus r\xE9cente \xE0 la plus ancienne.",
  "history.empty": "Aucune dose enregistr\xE9e pour cette s\xE9lection.",
  "history.open": "Historique complet des doses",
  "history.export": "Exporter en CSV",
  "history.filterMed": "M\xE9dicament",
  "history.filterStatus": "Statut",
  "history.allMeds": "Tous les m\xE9dicaments",
  "history.allStatuses": "Tous les statuts",
  "history.colDate": "Date",
  "history.colScheduled": "Pr\xE9vu \xE0",
  "history.colGiven": "Donn\xE9 \xE0",
  "history.colMedicine": "M\xE9dicament",
  "history.colDose": "Dose",
  "history.colStatus": "Statut",
  "history.colNote": "Note",
  "history.offlineLocalOnly": "Hors ligne \u2014 seules les saisies de cet appareil sont affich\xE9es.",
  "history.count": "{n} dose(s)",
  // --- Saisie rapide des doses ---
  "entry.summaryToLog": "{n} \xE0 saisir",
  "entry.summaryAllDone": "Tout est saisi",
  "entry.summaryRecorded": "{n} enregistr\xE9e(s)",
  "entry.bulkGive": "Les {n} donn\xE9s \xE0 {time}",
  "entry.scheduledMeta": "Pr\xE9vu {time}",
  "entry.scheduledMetaDose": "Pr\xE9vu {time} \xB7 {dose}",
  "entry.chipScheduled": "Pr\xE9vue \xB7 {time}",
  "entry.chipNow": "Maintenant \xB7 {time}",
  "entry.chipCustom": "Autre heure\u2026",
  "entry.exactTime": "Heure exacte",
  "entry.minus15": "Moins 15 minutes",
  "entry.plus15": "Plus 15 minutes",
  "entry.hintEmpty": "Tapez 745, 1930 ou 7h45",
  "entry.hintValid": "= {time} \xB7 Entr\xE9e pour enregistrer",
  "entry.hintInvalid": "Heure non reconnue \u2014 ex. 745 ou 7h45",
  "entry.notGiven": "Non donn\xE9",
  "entry.give": "Donn\xE9",
  "entry.giveAt": "Donn\xE9 \xE0 {time}",
  "entry.reasonLabel": "Motif :",
  "entry.toastGiven": "{name} \xB7 donn\xE9 \xE0 {time} \u2014 enregistr\xE9",
  "entry.toastReason": "{name} \xB7 {reason} \u2014 enregistr\xE9",
  "entry.toastBulk": "{n} doses donn\xE9es \xE0 {time} \u2014 enregistr\xE9",
  "entry.saveFailed": "\xC9chec de l\u2019enregistrement \u2014 r\xE9essayez",
  "entry.undo": "Annuler",
  // --- Moments de la journée ---
  "daypart.morning": "Matin",
  "daypart.afternoon": "Apr\xE8s-midi",
  "daypart.evening": "Soir",
  // --- Motifs (dose non donnée) ---
  "reason.refused": "Refus\xE9",
  "reason.forgotten": "Oubli\xE9",
  "reason.vomited": "Vomi",
  "reason.partial": "Partiel",
  "reason.other": "Autre motif"
};

// src/i18n/en.ts
var en = {
  // --- App / disclaimers (Safety §8) ---
  "app.name": "ToxoFollow",
  "app.tagline": "Congenital toxoplasmosis follow-up",
  "disclaimer.short": "This tool supports but does not replace medical judgment. In an emergency, contact the doctor or emergency services.",
  "disclaimer.firstLaunch": "ToxoFollow is a follow-up and decision-support tool. It is not a medical device and does not provide medical advice. It never diagnoses, prescribes, or modifies treatment on its own. Reminders are convenience alerts, not a guarantee of dose timing. In an emergency, contact your doctor or emergency services.",
  "disclaimer.report": "Generated by ToxoFollow. Supports but does not replace medical judgment. Doses shown must be validated by a doctor.",
  "banner.notValidated": "Clinical configuration not yet medically validated. Have the pediatrician review and sign off the configuration.",
  // --- Roles ---
  "role.caregiver": "Parent / caregiver",
  "role.clinician": "Clinician",
  "role.owner_admin": "Administrator",
  // --- Medication concepts (generic) ---
  "concept.pyrimethamine": "Pyrimethamine",
  "concept.sulfadiazine": "Sulfadiazine",
  "concept.folinic_acid": "Folinic acid (Leucovorin/Lederfoline)",
  "concept.folic_acid": "Folic acid (AcFol)",
  "concept.vitamin_d": "Vitamin D",
  "concept.supplemental_hydration": "Supplemental hydration",
  "concept.antibiotic": "Antibiotic",
  "concept.custom": "Custom medication",
  "meds.adjunct": "Adjunct / temporary",
  // --- Administration statuses ---
  "admin.taken": "Given",
  "admin.late": "Given late",
  "admin.missed": "Missed",
  "admin.vomited_after_dose": "Vomited after dose",
  "admin.partial": "Partial",
  "admin.refused": "Refused",
  "admin.held_by_doctor": "Held by doctor",
  "admin.not_applicable": "N/A",
  "admin.unknown": "Unknown",
  // --- Lab statuses ---
  "lab.ordered": "Ordered",
  "lab.drawn": "Drawn",
  "lab.pending": "Result pending",
  "lab.received": "Result received",
  "lab.redraw_needed": "Redraw needed",
  "lab.rejected": "Rejected",
  "lab.reviewed": "Reviewed by doctor",
  // --- Lab interpretations ---
  "interp.title": "Interpretation",
  "interp.no_change": "No change",
  "interp.repeat": "Repeat",
  "interp.dose_change": "Dose change",
  "interp.temporary_hold": "Temporary hold",
  "interp.stop": "Stop",
  "interp.consult_hematology": "Consult hematology",
  "interp.other": "Other",
  // --- Analytes ---
  "analyte.hemoglobin": "Hb (Hemoglobin)",
  "analyte.wbc": "WBC (White blood cells)",
  "analyte.neutrophils_abs": "ANC (absolute)",
  "analyte.neutrophils_pct": "Neutrophils (%)",
  "analyte.platelets": "Platelets",
  "analyte.lymphocytes_pct": "Lymphocytes (%)",
  "analyte.lymphocytes_abs": "Lymphocytes (absolute)",
  "analyte.reticulocytes": "Reticulocytes",
  "analyte.bilirubin": "Bilirubin",
  "analyte.alt": "ALT",
  "analyte.ast": "AST",
  "analyte.creatinine": "Creatinine",
  "analyte.urea": "Urea",
  "analyte.crp": "CRP",
  "analyte.custom": "Custom",
  // --- Serology ---
  "serology.igg": "IgG",
  "serology.igm": "IgM",
  "serology.iga": "IgA",
  "serology.pcr": "PCR",
  // --- Exam types ---
  "exam.nfs": "CBC (NFS)",
  "exam.weight_dose_review": "Weight & dose review",
  "exam.dilated_fundus": "Dilated fundus exam",
  "exam.etf": "Transfontanelle ultrasound (ETF)",
  "exam.mri": "MRI",
  "exam.baer_pea": "BAER/PEA (hearing)",
  "exam.pediatric_consult": "Pediatric consult",
  "exam.serology": "Serology",
  "exam.other": "Other",
  // --- Severity ---
  "severity.info": "Info",
  "severity.warning": "Warning",
  "severity.urgent": "Urgent",
  "severity.critical": "Critical",
  // --- Dose calculator warnings ---
  "dose.warn.staleWeight": "Weight is old ({days} days) \u2014 a dose review may be needed.",
  "dose.warn.belowMin": "Calculated dose below the configured minimum ({min} mg); minimum applied.",
  "dose.warn.aboveMax": "Calculated dose above the configured maximum ({max} mg); maximum applied.",
  "dose.warn.noConcentration": "Concentration (mg/mL) missing for a liquid form; volume cannot be computed.",
  "dose.warn.largeRounding": "Large rounding ({pct}%) vs the computed dose \u2014 please verify.",
  "dose.warn.zeroWeight": "Zero or negative weight \u2014 please check the entry.",
  "dose.draftNotice": "Calculated dose: draft for doctor approval. Not active until approved.",
  // --- Rule messages (defaults; clinician-configurable) ---
  "rule.pnn.belowThreshold": "ANC/PNN ({value} {unit}) below the configured threshold ({threshold} {thresholdUnit}).",
  "rule.hb.low": "Hemoglobin ({value} {unit}) below the alert level.",
  "rule.platelets.low": "Platelets ({value} {unit}) below the alert level.",
  "rule.trend.decline": "Sustained decline detected ({consecutiveDeclines} consecutive measurements).",
  "rule.lab.pendingTooLong": "Result pending for {daysPending} days \u2014 follow up with the lab.",
  "rule.lab.redrawNeeded": "Redraw needed.",
  "rule.exam.due": "{examType} exam coming up (due {dueDate}).",
  "rule.exam.overdue": "{examType} exam overdue by {overdueDays} days.",
  "rule.symptom.present": "Symptom reported: {symptom} ({date}).",
  "rule.g6pd.unknown": "G6PD status not on file \u2014 ask the doctor.",
  "rule.weight.stale": "Weight is old ({weightAgeDays} days) \u2014 consider a dose review.",
  // --- Default doctor-configured actions (Safety §4) ---
  "action.contactPediatrician": "Contact the treating pediatrician.",
  "action.scheduleLab": "Schedule the next blood draw.",
  "action.followUpLab": "Follow up with the lab for the result.",
  "action.bookExam": "Book an appointment for this exam.",
  "action.askDoctor": "Ask the doctor at the next consultation.",
  "action.doctorReview": "A medical review is needed.",
  // --- Weekly sheet / daily check-in ---
  "weekly.title": "Weekly tracking",
  "weekly.weekOf": "Week of",
  "weekly.weight": "Baby's weight (g)",
  "weekly.observations": "Observations for the week",
  "weekly.questions": "For the next consultation",
  "checkin.temperature": "Max temperature",
  "checkin.wetDiapers": "Wet diapers (#)",
  "checkin.stools": "Stools (#)",
  "checkin.feeding": "Feeding",
  "checkin.sleep": "Sleep / mood",
  "checkin.vomiting": "Vomiting",
  "checkin.rash": "Rash",
  "checkin.pallor": "Pallor",
  "checkin.bruising": "Bruising",
  "checkin.hydration": "Hydration",
  "checkin.appetite": "Appetite",
  // --- Symptoms ---
  "symptom.fever": "Fever",
  "symptom.pallor": "Pallor",
  "symptom.bruising": "Bruising",
  "symptom.rash": "Rash",
  "symptom.vomiting": "Vomiting",
  "symptom.vomiting_after_dose": "Vomiting after dose",
  "symptom.poor_feeding": "Poor feeding",
  "symptom.low_wet_diapers": "Low wet-diaper count",
  "symptom.dehydration_concern": "Dehydration concern",
  "symptom.lethargy": "Lethargy",
  "symptom.irritability": "Irritability",
  "symptom.other": "Other",
  // --- Growth / units ---
  "growth.weight": "Weight",
  "growth.headCircumference": "Head circumference (HC)",
  "weight.source.parent": "Parent",
  "weight.source.clinic": "Clinic",
  "weight.source.hospital": "Hospital",
  "weight.source.pharmacy": "Pharmacy",
  "weight.source.imported": "Imported",
  "weight.unverified": "Weight not verified",
  "weight.verified": "Weight verified",
  // --- G6PD / baseline ---
  "g6pd.unknown": "Unknown",
  "g6pd.pending": "Pending",
  "g6pd.normal": "Normal",
  "g6pd.abnormal": "Abnormal",
  "g6pd.not_applicable": "Not applicable",
  // --- Common UI ---
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.approve": "Approve",
  "common.reject": "Reject",
  "common.sign": "Sign",
  "common.today": "Today",
  "common.loading": "Loading\u2026",
  "common.error": "Something went wrong",
  "common.offline": "Offline",
  "common.synced": "Synced",
  "common.pendingSync": "Pending sync",
  // --- Order approval status ---
  "approval.draft": "Draft",
  "approval.pending_review": "Pending review",
  "approval.approved": "Approved",
  "approval.rejected": "Rejected",
  "approval.suspended": "Suspended",
  "approval.discontinued": "Discontinued",
  // --- Surveillance occurrence status ---
  "surv.upcoming": "Upcoming",
  "surv.due": "Due",
  "surv.overdue": "Overdue",
  "surv.done": "Done",
  // --- Frequency ---
  "freq.once_daily": "Once daily",
  "freq.twice_daily": "Twice daily",
  "freq.three_times_daily": "3\xD7/day",
  "freq.weekly": "Weekly",
  "freq.three_times_weekly": "3\xD7/week",
  "freq.every_other_day": "Every other day",
  "freq.every_x_hours": "Every X hours",
  "freq.specific_weekdays": "Specific weekdays",
  "freq.custom": "Custom",
  // --- Medication forms ---
  "form.tablet": "Tablet",
  "form.suspension": "Suspension",
  "form.compounded_liquid": "Compounded liquid",
  "form.capsule": "Capsule",
  "form.drops": "Drops",
  "form.powder": "Powder",
  "form.other": "Other",
  // --- Medications screen ---
  "med.title": "Medications",
  "med.new": "New medication",
  "med.concept": "Medication",
  "med.reason": "Reason",
  "med.suspend": "Suspend",
  "med.discontinue": "Discontinue",
  "med.current": "current",
  "med.folinic": "folinic",
  "med.folic": "folic",
  "med.folateWarning": "Folinic vs folic acid must never be conflated",
  "med.draftNotice": "New orders enter as a draft and require explicit approval before becoming active.",
  "med.brandName": "Brand / local name",
  "med.form": "Form",
  "med.concentration": "Concentration (mg/mL)",
  "med.doseAmount": "Dose amount",
  "med.doseUnit": "Dose unit",
  "med.frequency": "Frequency",
  "med.times": "Times (HH:mm, comma-separated)",
  "med.startDate": "Start date",
  "med.parentInstructions": "Parent instructions",
  "med.doseHistory": "Dose history",
  "med.dose": "Dose",
  "med.signed": "Signed",
  "med.reasonPrompt": "Reason for discontinuation?",
  // --- Common UI (additional) ---
  "common.delete": "Delete",
  "common.download": "Download",
  "common.edit": "Edit",
  "common.from": "From",
  "common.to": "To",
  "common.date": "Date",
  "common.status": "Status",
  "common.source": "Source",
  "common.size": "Size",
  "common.value": "Value",
  "common.unit": "Unit",
  "common.section": "Section",
  "common.summary": "Summary",
  "common.signed": "Signed",
  "common.next": "Next",
  "common.flags": "Flags",
  // --- Authentication (login screen) ---
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signIn": "Sign in",
  "auth.mfaCode": "MFA code (TOTP)",
  "auth.mfaRequired": "MFA code required",
  "auth.invalidCredentials": "Invalid credentials",
  "auth.parent": "Parent",
  "auth.clinicianAdmin": "Clinician / Admin",
  "auth.parentAccess": "Parent access by phone number",
  "auth.loginBySms": "SMS code",
  "auth.loginByPassword": "Email + password",
  "auth.smsAccess": "Sign in by SMS code \u2014 for parents and staff whose phone is registered.",
  "auth.phone": "Phone",
  "auth.codeReceived": "Code received",
  "auth.verifyCode": "Verify code",
  "auth.sendCode": "Send code",
  "auth.changeNumber": "Change number",
  "auth.codeSent": "If this number is authorized, a code was sent.",
  "auth.codeSendFailed": "Could not send the code",
  "auth.tooManyAttempts": "Too many attempts \u2014 please wait a minute and try again.",
  "auth.invalidCode": "Invalid or expired code",
  "auth.disclaimer": "This tool supports but does not replace medical judgment. In an emergency, contact the doctor.",
  // --- Dose review screen ---
  "dose.title": "Dose review",
  "dose.calculator": "Dose calculator",
  "dose.reviewQueue": "Review queue \u2014 drafts",
  "dose.ordersPendingReview": "Orders pending review",
  "dose.weightDate": "Weight date",
  "dose.mgPerKg": "mg/kg",
  "dose.dosesPerDay": "Doses/day",
  "dose.measurableIncrement": "Measurable increment",
  "dose.minDoseMg": "Min dose (mg)",
  "dose.maxDoseMg": "Max dose (mg)",
  "dose.perDayHint": "mg/kg is a daily amount (split across doses/day)",
  "dose.calculate": "Calculate",
  "dose.raw": "Raw",
  "dose.bounded": "Bounded",
  "dose.rounded": "Rounded",
  "dose.toOrder": "To order",
  "dose.recordOnly": "\u2014 (record only, no order)",
  "dose.editedAmount": "Edited amount",
  "dose.editedUnit": "Edited unit",
  "dose.reasonRequired": "Reason (required)",
  // Dose review — guided calculator UX
  "dose.calcSubtitle": "Compute a draft dose. Nothing is applied until you approve it below.",
  "dose.queueSubtitle": "Calculated drafts awaiting your approval.",
  "dose.ordersSubtitle": "Approved medications that still need a dose set.",
  "dose.step1": "1 \xB7 Baby\u2019s weight",
  "dose.step2": "2 \xB7 Medication",
  "dose.step3": "3 \xB7 Dosing rule",
  "dose.amountBasis": "The mg/kg you enter is per\u2026",
  "dose.perDose": "Per dose",
  "dose.perDay": "Per day",
  "dose.perDoseHint": "Each dose = mg/kg \xD7 weight.",
  "dose.advanced": "Advanced \u2014 bounds & rounding",
  "dose.incrementHint": "Smallest measurable step (e.g. 0.1 mL syringe graduation, 0.25 tablet).",
  "dose.solidNote": "Solid form \u2014 dosed in mg, no concentration needed.",
  "dose.preview": "Preview",
  "dose.perDoseShort": "dose",
  "dose.beforeRounding": "Before rounding",
  "dose.give": "Dose to give",
  // --- Labs / NFS screen (forms) ---
  "lab.sampleDate": "Sample date",
  "lab.resultDate": "Result date",
  "lab.refLow": "Ref low",
  "lab.refHigh": "Ref high",
  "lab.sentAbroad": "Sent abroad",
  "lab.abroad": "abroad",
  "lab.flags": "Flags",
  "lab.toVerify": "To verify",
  "lab.nothingToVerify": "Nothing to verify",
  "lab.viewScan": "View scan",
  "lab.verifySign": "Verify & sign",
  // --- Serology screen (form) ---
  "sero.qual": "Qual.",
  "sero.titer": "Titer",
  "sero.titerUnit": "Titer unit",
  "sero.qualitative": "Qualitative",
  "sero.marker": "Marker",
  // --- Attachments screen ---
  "attach.title": "Attachments",
  "attach.upload": "Upload",
  "attach.notice": "Files are encrypted at rest, with no public URLs. Downloads are audited.",
  "attach.file": "File",
  "attach.kind": "Kind",
  "attach.linkedType": "Linked type (optional)",
  "attach.linkedId": "Linked id (optional)",
  "attach.linked": "Linked",
  "attach.noFileSelected": "No file selected",
  // --- Appointments / Exams screen ---
  "appt.title": "Appointments / Exams",
  "appt.importEtf": "Import an ETF report",
  "appt.surveillanceCalendar": "Surveillance calendar (12 months)",
  "appt.appointments": "Appointments",
  "appt.findings": "Findings",
  "appt.nextDate": "Next date",
  "appt.specialist": "Specialist",
  "appt.reason": "Reason",
  "appt.examType": "Exam type",
  "appt.signed": "signed",
  "appt.status.scheduled": "Scheduled",
  "appt.status.completed": "Completed",
  "appt.status.missed": "Missed",
  "appt.status.cancelled": "Cancelled",
  "appt.status.rescheduled": "Rescheduled",
  // Parent-reported booking/attendance (distinct from clinician status)
  "apptConfirm.booked": "Booked",
  "apptConfirm.attended": "Attended",
  "apptConfirm.missed": "Missed",
  "appt.parentReportsAttended": "Family reports attended \u2014 review",
  // --- Observations / Questions screen ---
  "obs.title": "Observations / Questions",
  "obs.dailyCheckins": "Daily check-ins",
  "obs.symptoms": "Symptoms",
  // --- Adherence screen ---
  "adherence.title": "Adherence",
  "adherence.range": "Range",
  "adherence.perMedication": "Per medication",
  // --- Reports screen ---
  "reports.title": "Reports",
  "reports.exports": "Exports",
  "reports.encryptedZip": "Encrypted ZIP",
  "reports.icsCalendar": "calendar",
  "reports.pdfNotice": "The consultation PDF is generated on the parent phone (mobile app).",
  "reports.preview": "Preview",
  "reports.patient": "Patient",
  "reports.medications": "Medications",
  "reports.attachments": "Attachments",
  // --- Account / password (self change + admin reset) ---
  "account.title": "My account",
  "account.security": "Security",
  "account.signedInAs": "Signed in as {name}",
  "auth.changePassword": "Change password",
  "auth.currentPassword": "Current password",
  "auth.newPassword": "New password",
  "auth.confirmPassword": "Confirm password",
  "auth.passwordMismatch": "Passwords do not match.",
  "auth.passwordTooShort": "At least 12 characters.",
  "auth.passwordSame": "The new password must differ from the current one.",
  "auth.passwordChanged": "Password changed.",
  "auth.currentPasswordWrong": "Current password is incorrect.",
  // --- Admin: reset a user's password ---
  "admin.resetPassword": "Reset password",
  "admin.resetPasswordFor": "Reset password for {name}",
  "admin.newPasswordOptional": "New password (leave blank to generate one)",
  "admin.generatedPassword": "Temporary password (shown once)",
  "admin.tempPasswordOnce": "Share it with the user; it will not be shown again.",
  "admin.passwordReset": "Password reset.",
  "admin.cannotResetNoEmail": "No email \u2014 add one to allow a password",
  // --- Medications: edit + copy ---
  "med.copy": "Duplicate",
  "med.editTitle": "Edit medication",
  "med.copyTitle": "Duplicate medication",
  "med.reapprovalNeeded": "A clinical change returns the order to pending approval.",
  "med.localName": "Local name",
  "med.endDate": "End date",
  "med.doctorNotes": "Doctor notes",
  "med.safetyNotes": "Safety notes",
  "med.prescriber": "Prescriber",
  "med.doseMgPerKg": "Dose (mg/kg)",
  // --- Clinical notes (clinician free-form) ---
  "notes.title": "Clinical notes",
  "notes.subtitle": "Free-form doctor notes (G6PD, ETF, observations).",
  "notes.add": "Add a note",
  "notes.category": "Category",
  "notes.noteDate": "Date",
  "notes.text": "Note",
  "notes.placeholder": "Enter a clinical observation\u2026",
  "notes.empty": "No clinical notes.",
  "notes.saved": "Note saved.",
  "notes.delete": "Delete",
  "notes.deleted": "Note deleted.",
  "notes.by": "by {who}",
  "notes.cat.general": "General",
  "notes.cat.g6pd": "G6PD",
  "notes.cat.imaging": "Imaging / ETF",
  "notes.cat.exam": "Exam",
  "notes.cat.lab": "Lab",
  "notes.cat.other": "Other",
  "notes.visibleToFamily": "Visible to family",
  "notes.sharedWithFamily": "Shared with family",
  "notes.privateNote": "Private (doctor) note",
  "notes.shareWithFamily": "Share with family",
  "notes.hideFromFamily": "Hide from family",
  "notes.fromDoctor": "Notes from your doctor",
  "notes.importEtf": "Import a report (ETF / imaging)",
  "notes.importEtfHint": "Drop the report; the AI proposes a summary you review before saving.",
  "notes.aiPrivacy": "The file is analyzed by an AI service (Anthropic) configured by your facility.",
  "notes.analyze": "Analyze with AI",
  "notes.analyzing": "Analyzing\u2026",
  "notes.draftReview": "Draft proposed \u2014 review, then save.",
  "notes.aiUnavailable": "AI analysis unavailable (unsupported file type or AI disabled). Enter the summary manually.",
  "notes.saveAsNote": "Save as note",
  // --- Patient baseline editor (G6PD, allergies) ---
  "baseline.title": "Baseline facts",
  "baseline.edit": "Edit baseline facts",
  "baseline.g6pd": "G6PD status",
  "baseline.allergies": "Allergies (comma-separated)",
  "baseline.intolerances": "Intolerances (comma-separated)",
  "baseline.birthDetails": "Birth details",
  "baseline.notes": "Baseline notes",
  "baseline.saved": "Baseline facts saved.",
  // --- Navigation / shell ---
  "nav.settings": "Account",
  "nav.notes": "Clinical notes",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  // --- Day navigation (parent view) ---
  "day.title": "Day",
  "day.prev": "Previous day",
  "day.next": "Next day",
  "day.today": "Today",
  "day.backdated": "Entry for {date} (past day)",
  "day.noEntries": "Nothing was recorded for this day.",
  "day.editEntries": "Edit this day\u2019s entries",
  "day.summaryCheckin": "Daily check-in",
  "day.summaryGrowth": "Weight / head circumference",
  "day.summaryObservations": "Observations",
  // --- Administered medicine history ---
  "history.from": "From",
  "history.to": "To",
  "history.title": "Medicine history",
  "history.subtitle": "Every recorded dose, newest first.",
  "history.empty": "No doses recorded for this selection.",
  "history.open": "Full dose history",
  "history.export": "Export as CSV",
  "history.filterMed": "Medicine",
  "history.filterStatus": "Status",
  "history.allMeds": "All medicines",
  "history.allStatuses": "All statuses",
  "history.colDate": "Date",
  "history.colScheduled": "Scheduled",
  "history.colGiven": "Given at",
  "history.colMedicine": "Medicine",
  "history.colDose": "Dose",
  "history.colStatus": "Status",
  "history.colNote": "Note",
  "history.offlineLocalOnly": "Offline \u2014 showing only this device\u2019s entries.",
  "history.count": "{n} dose(s)",
  // --- Fast dose entry ---
  "entry.summaryToLog": "{n} to record",
  "entry.summaryAllDone": "All recorded",
  "entry.summaryRecorded": "{n} recorded",
  "entry.bulkGive": "All {n} given at {time}",
  "entry.scheduledMeta": "Due {time}",
  "entry.scheduledMetaDose": "Due {time} \xB7 {dose}",
  "entry.chipScheduled": "Scheduled \xB7 {time}",
  "entry.chipNow": "Now \xB7 {time}",
  "entry.chipCustom": "Another time\u2026",
  "entry.exactTime": "Exact time",
  "entry.minus15": "Minus 15 minutes",
  "entry.plus15": "Plus 15 minutes",
  "entry.hintEmpty": "Type 745, 1930 or 7h45",
  "entry.hintValid": "= {time} \xB7 Enter to save",
  "entry.hintInvalid": "Time not recognised \u2014 e.g. 745 or 7h45",
  "entry.notGiven": "Not given",
  "entry.give": "Given",
  "entry.giveAt": "Given at {time}",
  "entry.reasonLabel": "Reason:",
  "entry.toastGiven": "{name} \xB7 given at {time} \u2014 saved",
  "entry.toastReason": "{name} \xB7 {reason} \u2014 saved",
  "entry.toastBulk": "{n} doses given at {time} \u2014 saved",
  "entry.saveFailed": "Could not save \u2014 try again",
  "entry.undo": "Undo",
  // --- Parts of the day ---
  "daypart.morning": "Morning",
  "daypart.afternoon": "Afternoon",
  "daypart.evening": "Evening",
  // --- Reasons (dose not given) ---
  "reason.refused": "Refused",
  "reason.forgotten": "Forgotten",
  "reason.vomited": "Vomited",
  "reason.partial": "Partial",
  "reason.other": "Other reason"
};

// src/i18n/index.ts
var messages = { fr, en };
function t(locale, key, params) {
  const table = messages[locale] ?? messages[DEFAULT_LOCALE];
  let template = table[key] ?? messages[DEFAULT_LOCALE][key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      template = template.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return template;
}
function makeTranslator(locale) {
  return (key, params) => t(locale, key, params);
}
function allKeys() {
  return Object.keys(messages[DEFAULT_LOCALE]);
}
function missingKeys() {
  const frKeys = new Set(Object.keys(fr));
  const enKeys = new Set(Object.keys(en));
  return {
    missingInEn: [...frKeys].filter((k) => !enKeys.has(k)),
    missingInFr: [...enKeys].filter((k) => !frKeys.has(k))
  };
}
var calendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
var clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Expected HH:mm");
var decimalStringSchema = z.string().trim().regex(/^-?\d+(\.\d+)?$/, "Expected a decimal number as a string");
var positiveDecimalStringSchema = decimalStringSchema.refine(
  (s) => parseFloat(s) > 0,
  "Must be > 0"
);
var uuidSchema = z.string().uuid();
var localeSchema = z.enum(LOCALES);
var roleSchema = z.enum(ROLES);
var sexSchema = z.enum(SEX_VALUES);
var g6pdSchema = z.enum(G6PD_STATUSES);
var diagnosisStatusSchema = z.enum(DIAGNOSIS_STATUSES);
var medicationConceptSchema = z.enum(MEDICATION_CONCEPTS);
var medicationFormSchema = z.enum(MEDICATION_FORMS);
var routeSchema = z.enum(ROUTES);
var doseUnitSchema = z.enum(DOSE_UNITS);
var frequencyTypeSchema = z.enum(FREQUENCY_TYPES);
var weekdaySchema = z.enum(WEEKDAYS);
var orderApprovalStatusSchema = z.enum(ORDER_APPROVAL_STATUSES);
var administrationStatusSchema = z.enum(ADMINISTRATION_STATUSES);
var labStatusSchema = z.enum(LAB_STATUSES);
var labInterpretationSchema = z.enum(LAB_INTERPRETATIONS);
var analyteKeySchema = z.enum(ANALYTE_KEYS);
var analyteUnitSchema = z.enum(ANALYTE_UNITS);
var serologyMarkerSchema = z.enum(SEROLOGY_MARKERS);
var serologyQualitativeSchema = z.enum(SEROLOGY_QUALITATIVE);
var weightSourceSchema = z.enum(WEIGHT_SOURCES);
var examTypeSchema = z.enum(EXAM_TYPES);
var appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES);
var ruleTypeSchema = z.enum(RULE_TYPES);
var severitySchema = z.enum(SEVERITIES);
var recipientSchema = z.enum(RECIPIENTS);
var comparatorSchema = z.enum(COMPARATORS);
var symptomKeySchema = z.enum(SYMPTOM_KEYS);
var feedingTypeSchema = z.enum(FEEDING_TYPES);
var attachmentKindSchema = z.enum(ATTACHMENT_KINDS);
var reportTypeSchema = z.enum(REPORT_TYPES);
var exportFormatSchema = z.enum(EXPORT_FORMATS);
var scheduleSpecSchema = z.object({
  frequencyType: frequencyTypeSchema,
  times: z.array(clockTimeSchema).default([]),
  weekdays: z.array(weekdaySchema).optional(),
  intervalDays: z.number().int().positive().optional(),
  everyXHours: z.number().int().positive().max(24).optional(),
  anchorDate: calendarDateSchema.optional(),
  startDate: calendarDateSchema,
  endDate: calendarDateSchema.optional(),
  doseWindowMinutes: z.number().int().nonnegative().optional()
}).refine(
  (s) => s.frequencyType !== "specific_weekdays" && s.frequencyType !== "three_times_weekly" ? true : (s.weekdays?.length ?? 0) > 0,
  { message: "weekdays required for weekday-based schedules", path: ["weekdays"] }
);
var phoneSchema = z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, "Invalid phone number");
var emailSchema = z.string().trim().email();
var otpRequestSchema = z.object({ phone: phoneSchema });
var otpVerifySchema = z.object({
  phone: phoneSchema,
  code: z.string().trim().regex(/^\d{4,8}$/)
});
var passwordSchema = z.string().min(12, "Password must be at least 12 characters").max(200);
var clinicianLoginSchema = z.object({ email: emailSchema, password: z.string().min(1) });
var totpSchema = z.string().regex(/^\d{6}$/);
var changePasswordSchema = z.object({ currentPassword: z.string().min(1), newPassword: passwordSchema }).refine((d) => d.currentPassword !== d.newPassword, {
  message: "New password must differ from the current one",
  path: ["newPassword"]
});
var adminResetPasswordSchema = z.object({ newPassword: passwordSchema.optional() });
var adminUserUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
  role: roleSchema.optional(),
  email: emailSchema.nullish(),
  phone: phoneSchema.nullish(),
  locale: localeSchema.optional(),
  // Recovery: an admin can DISABLE a user's MFA (e.g. lost authenticator).
  // The user re-enrolls themselves; an admin can never enable it for them.
  disableMfa: z.boolean().optional()
}).refine((d) => Object.values(d).some((v) => v !== void 0), {
  message: "Provide at least one field to update"
});
var patientKeyFactsSchema = z.object({
  name: z.string().trim().min(1),
  nickname: z.string().trim().optional(),
  dob: calendarDateSchema,
  sex: sexSchema.default("unknown"),
  bloodType: z.string().trim().optional(),
  facility: z.string().trim().optional(),
  diagnosis: z.string().trim().optional(),
  diagnosisStatus: diagnosisStatusSchema.default("suspected"),
  treatmentStartDate: calendarDateSchema.optional()
});
var patientBaselineSchema = z.object({
  g6pdStatus: g6pdSchema.default("unknown"),
  allergies: z.array(z.string().trim()).default([]),
  intolerances: z.array(z.string().trim()).default([]),
  birthDetails: z.string().trim().optional(),
  notes: z.string().trim().optional()
});
var clinicalNoteCategorySchema = z.enum(CLINICAL_NOTE_CATEGORIES);
var clinicalNoteInputSchema = z.object({
  noteDate: calendarDateSchema.optional(),
  // defaults to today server-side
  category: clinicalNoteCategorySchema.default("general"),
  title: z.string().trim().max(200).optional(),
  text: z.string().trim().min(1).max(1e4),
  visibleToFamily: z.boolean().default(true),
  relatedEntity: z.string().trim().max(40).optional(),
  relatedId: uuidSchema.optional()
});
var clinicalNoteUpdateSchema = z.object({
  noteDate: calendarDateSchema.optional(),
  category: clinicalNoteCategorySchema.optional(),
  title: z.string().trim().max(200).nullish(),
  text: z.string().trim().min(1).max(1e4).optional(),
  visibleToFamily: z.boolean().optional()
});
var medicationOrderInputSchema = z.object({
  concept: medicationConceptSchema,
  brandName: z.string().trim().optional(),
  localName: z.string().trim().optional(),
  route: routeSchema.default("oral"),
  form: medicationFormSchema,
  concentrationMgPerMl: positiveDecimalStringSchema.optional(),
  doseAmount: positiveDecimalStringSchema.optional(),
  doseUnit: doseUnitSchema.optional(),
  doseMgPerKg: positiveDecimalStringSchema.optional(),
  schedule: scheduleSpecSchema,
  startDate: calendarDateSchema,
  endDate: calendarDateSchema.optional(),
  isAdjunct: z.boolean().default(false),
  parentInstructions: z.string().trim().optional(),
  doctorNotes: z.string().trim().optional(),
  prescriber: z.string().trim().optional(),
  safetyNotes: z.string().trim().optional(),
  // New orders enter as draft/pending; only a clinician can set "approved".
  approvalStatus: orderApprovalStatusSchema.default("draft")
});
var doseChangeInputSchema = z.object({
  orderId: uuidSchema,
  doseAmount: positiveDecimalStringSchema,
  doseUnit: doseUnitSchema,
  effectiveDate: calendarDateSchema,
  reason: z.string().trim().min(1),
  schedule: scheduleSpecSchema.optional()
});
var administrationInputSchema = z.object({
  orderId: uuidSchema,
  scheduledDate: calendarDateSchema,
  scheduledTime: clockTimeSchema.optional(),
  status: administrationStatusSchema,
  actualDate: calendarDateSchema.optional(),
  actualTime: clockTimeSchema.optional(),
  doseGiven: decimalStringSchema.optional(),
  doseUnit: doseUnitSchema.optional(),
  note: z.string().trim().optional(),
  // Client-generated id for idempotent offline sync.
  clientId: z.string().trim().min(1)
});
var administrationBulkInputSchema = z.object({
  entries: z.array(administrationInputSchema).min(1).max(20)
});
var doseFormulaInputSchema = z.object({
  mgPerKg: positiveDecimalStringSchema,
  perDay: z.boolean().optional(),
  dosesPerDay: z.number().int().positive().optional(),
  minDoseMg: positiveDecimalStringSchema.optional(),
  maxDoseMg: positiveDecimalStringSchema.optional()
});
var doseCalcInputSchema = z.object({
  weightGrams: positiveDecimalStringSchema,
  weightDate: calendarDateSchema,
  concept: medicationConceptSchema,
  form: medicationFormSchema,
  concentrationMgPerMl: positiveDecimalStringSchema.nullish(),
  formula: doseFormulaInputSchema,
  formulaVersionId: z.string().trim().min(1),
  measurableIncrement: positiveDecimalStringSchema.optional(),
  roundingDirection: z.enum(["nearest", "down", "up"]).optional(),
  evaluationDate: calendarDateSchema.optional(),
  staleWeightDays: z.number().int().positive().optional()
});
var doseApprovalSchema = z.object({
  calculationId: uuidSchema,
  decision: z.enum(["approve", "edit", "reject"]),
  editedAmount: positiveDecimalStringSchema.optional(),
  editedUnit: doseUnitSchema.optional(),
  effectiveDate: calendarDateSchema.optional(),
  reason: z.string().trim().optional()
});
var analyteValueSchema = z.object({
  key: analyteKeySchema,
  customLabel: z.string().trim().optional(),
  // A decimal number OR a qualitative result (e.g. "Positive", "Negative").
  // Numeric-only operations (range flags, trends, dose math) guard on
  // isNumericValue() so a qualitative value never enters the math.
  value: z.string().trim().min(1).max(64),
  unit: analyteUnitSchema,
  customUnit: z.string().trim().max(32).nullish(),
  referenceLow: decimalStringSchema.nullish(),
  referenceHigh: decimalStringSchema.nullish()
});
var labResultInputSchema = z.object({
  sampleDate: calendarDateSchema,
  resultDate: calendarDateSchema.nullish(),
  status: labStatusSchema.default("ordered"),
  analytes: z.array(analyteValueSchema).default([]),
  interpretation: labInterpretationSchema.optional(),
  interpretationNote: z.string().trim().optional(),
  isNfsPreset: z.boolean().default(false)
});
var serologyResultInputSchema = z.object({
  sampleDate: calendarDateSchema,
  resultDate: calendarDateSchema.nullish(),
  marker: serologyMarkerSchema,
  qualitative: serologyQualitativeSchema.optional(),
  titer: decimalStringSchema.optional(),
  titerUnit: z.string().trim().optional(),
  interpretation: z.string().trim().optional()
});
var growthEntryInputSchema = z.object({
  date: calendarDateSchema,
  weightGrams: positiveDecimalStringSchema.optional(),
  weightSource: weightSourceSchema.optional(),
  weightVerified: z.boolean().default(false),
  headCircumferenceCm: positiveDecimalStringSchema.optional(),
  note: z.string().trim().optional()
}).refine((g) => g.weightGrams != null || g.headCircumferenceCm != null, {
  message: "Provide at least a weight or a head circumference"
});
var appointmentInputSchema = z.object({
  date: calendarDateSchema,
  examType: examTypeSchema,
  specialist: z.string().trim().optional(),
  reason: z.string().trim().optional(),
  findings: z.string().trim().optional(),
  status: appointmentStatusSchema.default("scheduled"),
  nextDate: calendarDateSchema.optional(),
  headCircumferenceCm: positiveDecimalStringSchema.optional()
});
var dailyCheckInInputSchema = z.object({
  date: calendarDateSchema,
  maxTemperatureC: decimalStringSchema.optional(),
  wetDiapers: z.number().int().nonnegative().optional(),
  stools: z.number().int().nonnegative().optional(),
  feedingType: feedingTypeSchema.optional(),
  feedingNote: z.string().trim().optional(),
  sleepMood: z.string().trim().optional(),
  symptoms: z.array(symptomKeySchema).default([]),
  note: z.string().trim().optional(),
  clientId: z.string().trim().min(1)
});
var observationInputSchema = z.object({
  date: calendarDateSchema,
  text: z.string().trim().min(1),
  clientId: z.string().trim().min(1)
});
var consultationQuestionInputSchema = z.object({
  text: z.string().trim().min(1),
  clientId: z.string().trim().min(1)
});
var answerQuestionSchema = z.object({
  questionId: uuidSchema,
  answer: z.string().trim().min(1)
});
var ruleDefinitionInputSchema = z.object({
  type: ruleTypeSchema,
  enabled: z.boolean().default(true),
  severity: severitySchema,
  recipients: recipientSchema,
  messageKey: z.string().trim().min(1),
  actionKey: z.string().trim().min(1),
  analyteKey: analyteKeySchema.optional(),
  comparator: comparatorSchema.optional(),
  threshold: decimalStringSchema.optional(),
  thresholdUnit: analyteUnitSchema.optional(),
  declineCount: z.number().int().positive().optional(),
  examType: examTypeSchema.optional(),
  overdueGraceDays: z.number().int().nonnegative().optional(),
  symptomKey: symptomKeySchema.optional(),
  pendingResultDays: z.number().int().positive().optional()
});
var attachmentMetaSchema = z.object({
  kind: attachmentKindSchema,
  filename: z.string().trim().min(1),
  contentType: z.string().trim().min(1),
  linkedType: z.enum(["lab", "appointment", "medication", "note", "profile", "serology"]).optional(),
  linkedId: uuidSchema.optional()
});
var inviteSchema = z.object({
  role: z.enum(["caregiver", "clinician"]),
  name: z.string().trim().min(1),
  email: emailSchema.optional(),
  phone: phoneSchema.optional(),
  locale: localeSchema.default("fr")
});
var syncPushSchema = z.object({
  lastSyncedAt: z.string().datetime().nullish(),
  changes: z.array(
    z.object({
      entity: z.string().trim().min(1),
      clientId: z.string().trim().min(1),
      op: z.enum(["upsert", "soft_delete"]),
      updatedAt: z.string().datetime(),
      payload: z.record(z.unknown())
    })
  )
});
var reportRequestSchema = z.object({
  type: reportTypeSchema,
  locale: localeSchema.default("fr"),
  fromDate: calendarDateSchema.optional(),
  toDate: calendarDateSchema.optional()
});

export { ADHERENT_STATUSES, ADMINISTRATION_STATUSES, ALERT_STATUSES, ANALYTE_KEYS, ANALYTE_UNITS, APPOINTMENT_PARENT_CONFIRM, APPOINTMENT_STATUSES, APP_NAME, ATTACHMENT_KINDS, AUDIT_ACTIONS, CLINICAL_NOTE_CATEGORIES, COMPARATORS, DAY_CURSOR_FALLBACK_DAYS, DEFAULT_BRAND_NAMES, DEFAULT_LATE_WINDOW_MINUTES, DEFAULT_LOCALE, DEFAULT_MEASURABLE_INCREMENT, DEFAULT_PENDING_RESULT_DAYS, DEFAULT_STALE_WEIGHT_DAYS, DIAGNOSIS_STATUSES, DOSE_GRACE_MINUTES, DOSE_UNITS, EXAM_TYPES, EXPORT_FORMATS, FEEDING_TYPES, FOLATE_CONCEPTS, FREQUENCY_TYPES, G6PD_STATUSES, INVITATION_STATUSES, LAB_INTERPRETATIONS, LAB_STATUSES, LOCALES, MEDICATION_CONCEPTS, MEDICATION_FORMS, MS_PER_DAY, MS_PER_HOUR, MS_PER_MINUTE, NONADHERENT_STATUSES, NOTIFICATION_CHANNELS, ORDER_APPROVAL_STATUSES, REASON_STATUS, RECIPIENTS, REMINDER_STATES, REMINDER_TYPES, REPORT_TYPES, ROLES, ROUTES, RULE_TYPES, SEROLOGY_MARKERS, SEROLOGY_QUALITATIVE, SEVERITIES, SEX_VALUES, SURVEILLANCE_STATUSES, SYMPTOM_KEYS, TREND_DIRECTIONS, WEEKDAYS, WEIGHT_SOURCES, absoluteFromPercent, add, addDays, addMonths, addWeeks, adherentStatuses, adminResetPasswordSchema, adminUserUpdateSchema, administrationBulkInputSchema, administrationInputSchema, administrationStatusSchema, ageInMonths, allKeys, analyteKeySchema, analyteUnitSchema, analyteValueSchema, answerQuestionSchema, appointmentInputSchema, appointmentStatusSchema, areUnitsCompatible, attachmentKindSchema, attachmentMetaSchema, buildSurveillanceCalendar, calculateDose, calendarDateSchema, canStepDay, changePasswordSchema, clampDay, clinicalNoteCategorySchema, clinicalNoteInputSchema, clinicalNoteUpdateSchema, clinicianLoginSchema, clockTimeSchema, clockToMinutes, combineToInstant, comparatorSchema, compareAnalyte, compareDates, computeAdherence, conceptsInSchedule, consecutiveDeclines, consultationQuestionInputSchema, convertAnalyte, dailyCheckInInputSchema, dayBounds, dayPartOf, dec, decimalStringSchema, defaultCadenceRules, deriveGivenStatus, detectMissedDoses, diagnosisStatusSchema, diffDays, diffMinutes, diffWeeks, div, doseApprovalSchema, doseCalcInputSchema, doseChangeInputSchema, doseFormulaInputSchema, doseUnitSchema, emailSchema, evaluateComparator, evaluateLabStatus, evaluateRules, examTypeSchema, exportFormatSchema, feedingTypeSchema, flagAnalytes, frequencyTypeSchema, g6pdSchema, gramsToKg, groupDosesBySlot, growthEntryInputSchema, inviteSchema, isCalendarDate, isClockTime, isDecimalLike, isNumericValue, kgToGrams, labInterpretationSchema, labResultInputSchema, labStatusSchema, localeSchema, makeTranslator, maxDate, medicationConceptSchema, medicationFormSchema, medicationOrderInputSchema, mergeDecision, mergeKey, messages, minDate, missingKeys, mul, nextOccurrence, observationInputSchema, occurrencesBetween, occursOn, orderApprovalStatusSchema, otpRequestSchema, otpVerifySchema, parseCalendarDate, parseClock, passwordSchema, patientBaselineSchema, patientKeyFactsSchema, phoneSchema, positiveDecimalStringSchema, recipientSchema, reportRequestSchema, reportTypeSchema, roleSchema, roundTo, roundToIncrement, routeSchema, ruleDefinitionInputSchema, ruleTypeSchema, scheduleSpecSchema, scheduledDosesForDate, scheduledDosesForRange, serologyMarkerSchema, serologyQualitativeSchema, serologyResultInputSchema, severitySchema, sexSchema, shiftClock, slotKey, sortTimes, staleWeightFlag, stepDay, sub, symptomKeySchema, syncPushSchema, t, timesOn, toCalendarDate, toStorageString, totpSchema, trendDirection, unitFamily, uuidSchema, weekdayOf, weekdaySchema, weightSourceSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map